/**
 * Signature Series Product Configurator JavaScript
 * Handles variant selection, step transitions, and configuration options
 * for panel-based modular building products
 */

(function() {
'use strict';

const DEBUG = false;
const log = (...args) => DEBUG && console.log(...args);

// ============================================================================
// UTILITIES
// ============================================================================

const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => ctx.querySelectorAll(sel);

const q = (dataAttr, classSelector, ctx = document) => 
  $(dataAttr, ctx) || $(classSelector, ctx);

const qa = (dataAttr, classSelector, ctx = document) => {
  const result = $$(dataAttr, ctx);
  return result.length > 0 ? result : $$(classSelector, ctx);
};

// DRY: shared in `assets/configurator-shared.js`
const normalizeNumber = (...args) => window.ConfiguratorShared.normalizeNumber(...args);
const formatPrice = (dollars) => window.ConfiguratorShared.formatMoneyFromDollars(dollars, { noDecimals: true });

/** Format option value for display (kebab-case to Title Case) */
const formatOptionValue = (value) => {
  if (!value) return '';
  return value.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};

/** Format section key to display name */
const formatSectionName = (key) => key.split(/[-_]/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');

// ============================================================================
// SIGNATURE CONFIGURATOR CLASS
// ============================================================================

class SignatureConfigurator {
  constructor() {
    this.step1Section = q('[data-configurator-step="1"]', '.signature-step-1');
    
    // Find step 2 section - first try the element, then find by wrapper ID
    this.step2Section = q('[data-configurator-step="2"]', '[data-signature-step-2]');
    if (!this.step2Section) {
      // Find by section wrapper ID pattern
      const step2Wrapper = $('[id*="signature_step_2"]') || $('[id*="signature-step-2"]');
      if (step2Wrapper) {
        // Create a placeholder element if the section content is empty
        if (!step2Wrapper.querySelector('section')) {
          const placeholder = document.createElement('section');
          placeholder.className = 'signature-step-2';
          placeholder.setAttribute('data-signature-step-2', '');
          placeholder.setAttribute('data-configurator-step', '2');
          placeholder.setAttribute('data-section-id', step2Wrapper.id.replace('shopify-section-', ''));
          placeholder.style.display = 'none';
          step2Wrapper.appendChild(placeholder);
        }
        this.step2Section = step2Wrapper.querySelector('section') || step2Wrapper;
        // Store the wrapper ID for later use in fetch
        this.step2SectionId = step2Wrapper.id.replace('shopify-section-', '');
      }
    }
    
    if (!this.step1Section) return;

    this.variantCards = qa('[data-variant-card]', '.signature-variant-card', this.step1Section);
    this.priceDisplay = $('[data-product-price]', this.step1Section);
    this.comparePriceDisplay = $('[data-compare-price]', this.step1Section);
    this.shellPriceDisplay = $('[data-shell-price]', this.step1Section);
    this.thumbnails = qa('[data-product-thumbnail]', '.signature-thumbnail', this.step1Section);
    
    // Get main image from Swiper structure or fallback to original selector
    const mainSwiperEl = this.step1Section?.querySelector('[data-product-main-swiper]');
    if (mainSwiperEl) {
      const firstSlide = mainSwiperEl.querySelector('swiper-slide');
      this.mainImage = firstSlide?.querySelector('[data-product-main-image]') || 
                       q('[data-product-main-image]', '.signature-main-image', this.step1Section);
    } else {
      this.mainImage = q('[data-product-main-image]', '.signature-main-image', this.step1Section);
    }
    
    this.radioButtons = $$('input[type="radio"][name="variant-selection"]', this.step1Section);
    this.designOwnBtn = $('[data-design-your-own]', this.step1Section);

    this.currentConfig = {};
    this.basePrice = 0;
    this.configPrice = 0;
    this.permitPlanSelected = 'exclude';
    this.permitPlanPrice = 0;
    this.foundationTypeSelected = 'wood-frame';
    this.userFormData = null;
    this.finalPriceData = null;
    this.finalPriceFetchTimer = null;
    this.finalPriceAbort = null;
    this.finalPriceLastPayloadKey = null;
    this.steps = [];
    this.currentStep = 1;
    this.modelConfig = null;
    this.sectionSettings = null;

    if (this.variantCards.length === 0) return;
    this.init();
  }

  init() {
    this.setupVariantSelection();
    this.setupRadioChangeEvents();
    this.setupThumbnailSwitching();
    this.setupDesignOwnButton();
    if (this.step2Section) this.setupStep2();
  }

  // ============================================================================
  // STEP 1: VARIANT SELECTION
  // ============================================================================

  setupVariantSelection() {
    this.variantCards.forEach(card => {
      card.addEventListener('click', (e) => {
        if (e.target.type === 'radio') return;
        this.handleVariantSelection(card);
      });
    });
  }

  setupRadioChangeEvents() {
    this.radioButtons.forEach(radio => {
      radio.addEventListener('change', (e) => {
        if (e.target.checked) {
          const card = e.target.closest('[data-variant-card], .signature-variant-card');
          if (card) this.handleVariantSelection(card);
        }
      });
    });
  }

  handleVariantSelection(card) {
    this.variantCards.forEach(c => c.classList.remove('selected'));
    card.classList.add('selected');

    const radio = card.querySelector('input[type="radio"]');
    if (radio && !radio.checked) radio.checked = true;

    const { variantId, variantPriceFormatted, variantShellPriceFormatted, variantPrice, variantCompareAtPrice } = card.dataset;
    this.basePrice = parseInt(variantPrice, 10);

    if (this.priceDisplay && variantPriceFormatted) {
      this.priceDisplay.textContent = variantPriceFormatted;
    }

    // Update compare-at price
    if (this.comparePriceDisplay) {
      if (variantCompareAtPrice) {
        this.comparePriceDisplay.textContent = variantCompareAtPrice;
        this.comparePriceDisplay.style.display = '';
      } else {
        this.comparePriceDisplay.style.display = 'none';
      }
    }

    // Update shell only price 
    if (this.shellPriceDisplay && variantShellPriceFormatted) {
      this.shellPriceDisplay.textContent = variantShellPriceFormatted;
    }

    if (variantId) {
      this.updateURL(variantId);
      this.dispatchVariantChange(variantId, this.basePrice);
    }
  }

  setupDesignOwnButton() {
    if (!this.designOwnBtn) {
      const buttons = $$('button, a', this.step1Section);
      buttons.forEach(btn => {
        if (btn.textContent.toLowerCase().includes('design your own')) {
          this.designOwnBtn = btn;
        }
      });
    }

    if (this.designOwnBtn) {
      this.designOwnBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.hideHeader();
        this.showStep2();
      });
    }
  }

  hideHeader() {
    const topbar = document.querySelector('[data-top-bar], .topbar');
    if (topbar) {
      // Add transition for smooth animation with longer delay
      topbar.style.setProperty('transition', 'opacity 0.5s ease-out, transform 0.5s ease-out, max-height 0.5s ease-out', 'important');
      topbar.style.setProperty('opacity', '0', 'important');
      topbar.style.setProperty('transform', 'translateY(-100%)', 'important');
      topbar.style.setProperty('max-height', '0', 'important');
      topbar.style.setProperty('overflow', 'hidden', 'important');
      // Hide completely after animation
      setTimeout(() => {
        topbar.style.setProperty('display', 'none', 'important');
      }, 500);
    }
  }

  showHeader() {
    const topbar = document.querySelector('[data-top-bar], .topbar');
    if (topbar) {
      // Show element first
      topbar.style.setProperty('display', '', 'important');
      // Get original height for smooth transition
      const originalHeight = topbar.scrollHeight;
      topbar.style.setProperty('max-height', originalHeight + 'px', 'important');
      // Add transition for smooth animation with longer delay
      topbar.style.setProperty('transition', 'opacity 0.5s ease-in, transform 0.5s ease-in, max-height 0.5s ease-in', 'important');
      // Trigger reflow to ensure initial state is applied
      void topbar.offsetHeight;
      // Animate in
      requestAnimationFrame(() => {
        topbar.style.setProperty('opacity', '1', 'important');
        topbar.style.setProperty('transform', 'translateY(0)', 'important');
        topbar.style.setProperty('overflow', '', 'important');
      });
    }
  }

  setupThumbnailSwitching() {
    const mainSwiperEl = this.step1Section?.querySelector('[data-product-main-swiper]');
    const thumbnailsSwiperEl = this.step1Section?.querySelector('[data-product-thumbnails-swiper]');
    
    if (!mainSwiperEl || !thumbnailsSwiperEl) {
      if (!this.thumbnails.length || !this.mainImage) return;
      this.thumbnails.forEach(thumb => {
        thumb.addEventListener('click', () => {
          this.thumbnails.forEach(t => t.classList.remove('active'));
          thumb.classList.add('active');
          const newSrc = thumb.dataset.mediaSrc;
          if (this.mainImage && newSrc) this.mainImage.src = newSrc;
        });
      });
      return;
    }

    let attempts = 0;
    const maxAttempts = 60;
    
    const initializeSwipers = () => {
      if (mainSwiperEl.swiper && thumbnailsSwiperEl.swiper) {
        const mainSwiper = mainSwiperEl.swiper;
        const thumbnailsSwiper = thumbnailsSwiperEl.swiper;

        this.thumbnails.forEach((thumb, index) => {
          thumb.addEventListener('click', () => {
            this.thumbnails.forEach(t => t.classList.remove('active'));
            thumb.classList.add('active');
            const slideIndex = parseInt(thumb.dataset.slideIndex || index, 10);
            mainSwiper.slideTo(slideIndex);
            thumbnailsSwiper.slideTo(index);
            const mainSlide = mainSwiper.slides[slideIndex];
            const mainImg = mainSlide?.querySelector('[data-product-main-image]');
            if (mainImg) {
              this.mainImage = mainImg;
            }
          });
        });

        mainSwiper.on('slideChange', () => {
          const activeIndex = mainSwiper.activeIndex;
          this.thumbnails.forEach((thumb, index) => {
            const slideIndex = parseInt(thumb.dataset.slideIndex || index, 10);
            if (slideIndex === activeIndex) {
              this.thumbnails.forEach(t => t.classList.remove('active'));
              thumb.classList.add('active');
              thumbnailsSwiper.slideTo(index);
            }
          });
          const activeSlide = mainSwiper.slides[activeIndex];
          const mainImg = activeSlide?.querySelector('[data-product-main-image]');
          if (mainImg) {
            this.mainImage = mainImg;
          }
        });

        thumbnailsSwiper.on('slideChange', () => {
          const activeThumbIndex = thumbnailsSwiper.activeIndex;
          const thumb = this.thumbnails[activeThumbIndex];
          if (thumb) {
            const slideIndex = parseInt(thumb.dataset.slideIndex || activeThumbIndex, 10);
            mainSwiper.slideTo(slideIndex);
          }
        });
      } else if (attempts < maxAttempts) {
        attempts++;
        requestAnimationFrame(initializeSwipers);
      }
    };

    initializeSwipers();
  }

  // ============================================================================
  // STEP 2: CONFIGURATOR
  // ============================================================================

  async showStep2() {
    const selectedVariantId = this.getSelectedVariantId();
    if (!selectedVariantId) return;

    this.showLoadingState();

    try {
      const sectionHTML = await this.fetchStep2Section(selectedVariantId);
      if (sectionHTML) {
        this.updateStep2Content(sectionHTML);
        document.body.classList.add('configurator-active');
        this.toggleStep2HiddenSections(false); // Hide extra sections in step 2
        this.setupStep2();
        this.loadStep2Data();
        this.hideLoadingState();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        ConfiguratorShared.StepHistoryManager.pushStep2(this);
      } else {
        this.hideLoadingState();
      }
    } catch (error) {
      console.error('Error loading Step 2:', error);
      this.hideLoadingState();
    }
  }

  /**
   * Toggle visibility of sections that should be hidden in step 2
   * @param {boolean} show - true to show sections, false to hide
   */
  toggleStep2HiddenSections(show) {
    const footerForm = document.querySelector('div.newsletter-wrapper');
    
    if (footerForm) {
      footerForm.style.display = show ? '' : 'none';
    }
    
    const mainContent = document.getElementById('MainContent');
    if (!mainContent) return;

    // Find all Shopify sections
    const allSections = mainContent.querySelectorAll('[id^="shopify-section-"]');
    
    allSections.forEach(section => {
      const sectionId = section.id;
      
      // Only keep step 2 sections visible, hide everything else
      // Exclude FAQ section - it's controlled separately by toggleFaqSection
      const isStep2Section = sectionId.includes('signature_step_2') || sectionId.includes('signature-step-2');
      const isFaqSection = sectionId.includes('global_modal_faq') || sectionId.includes('global-modal-faq');
      
      if (!isStep2Section && !isFaqSection) {
        section.style.display = show ? '' : 'none';
      }
    });
  }

  async fetchStep2Section(variantId) {
    // Try multiple sources for the section ID
    let sectionId = this.step2SectionId || this.step2Section?.dataset?.sectionId;
    
    if (!sectionId) {
      const wrapper = this.step2Section?.closest('[id^="shopify-section-"]');
      if (wrapper) {
        sectionId = wrapper.id.replace('shopify-section-', '');
      }
    }
    
    if (!sectionId) {
      // Last resort: find any section wrapper with signature_step_2 in the ID
      const fallbackWrapper = $('[id*="signature_step_2"]') || $('[id*="signature-step-2"]');
      if (fallbackWrapper) {
        sectionId = fallbackWrapper.id.replace('shopify-section-', '');
      }
    }
    
    if (!sectionId) {
      throw new Error('Could not determine section ID for step 2');
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', sectionId);

    log('Fetching step 2 section:', url.toString());

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.text();
  }

  updateStep2Content(html) {
    if (!html || !this.step2Section) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const newSection = doc.querySelector('[data-configurator-step="2"]') ||
                       doc.querySelector('[data-signature-step-2]') ||
                       doc.querySelector('.signature-step-2') ||
                       doc.querySelector('section');

    if (newSection) {
      this.step2Section.innerHTML = newSection.innerHTML;
      if (newSection.dataset.sectionId) {
        this.step2Section.dataset.sectionId = newSection.dataset.sectionId;
      }
      this.step2Section.style.display = 'block';
    } else {
      const bodyContent = doc.body.innerHTML;
      if (bodyContent) {
        this.step2Section.innerHTML = bodyContent;
        this.step2Section.style.display = 'block';
      }
    }
  }

  showLoadingState() {
    if (this.designOwnBtn) {
      this.designOwnBtn.dataset.originalText = this.designOwnBtn.textContent;
      this.designOwnBtn.textContent = 'Loading...';
      this.designOwnBtn.disabled = true;
    }
  }

  hideLoadingState() {
    if (this.designOwnBtn) {
      this.designOwnBtn.textContent = this.designOwnBtn.dataset.originalText || 'Design Your Own';
      this.designOwnBtn.disabled = false;
    }
  }

  hideStep2({ fromPopstate = false } = {}) {
    if (!fromPopstate) {
      ConfiguratorShared.StepHistoryManager.exitStep2();
      return;
    }

    document.body.classList.remove('configurator-active');
    this.toggleStep2HiddenSections(true); // Show extra sections when leaving step 2
    if (this.viewer3D) {
      this.viewer3D.dispose();
      this.viewer3D = null;
    }
    // Restore layout to 8-4 columns before hiding
    this.restoreDefaultLayout();

    // Remove product-summary-tab class when closing configurator (Signature-specific)
    const configuratorMain = $('[data-configurator-main]', this.step2Section);
    if (configuratorMain) {
      configuratorMain.classList.remove('product-summary-tab');
    }

    if (this.step2Section) this.step2Section.style.display = 'none';
    this.hideLoadingState();
    this.showHeader();
    this.toggleFaqSection(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  toggleFaqSection(show) {
    const faqSectionWrapper = document.querySelector('[id^="shopify-section-"][id*="global_modal_faq"], [id^="shopify-section-"][id*="global-modal-faq"]');
    const faqSection = document.querySelector('.global-modal-faq-section');
    const faqFooter = document.querySelector('.modal-faq-footer');
    const siteFooter = document.querySelector('.site-footer');
    if (show) {
      if (faqSectionWrapper) faqSectionWrapper.style.setProperty('display', 'block', 'important');
      if (faqSection) faqSection.style.setProperty('display', 'block', 'important');
      if (faqFooter) faqFooter.style.setProperty('display', 'block', 'important');
      if (siteFooter) siteFooter.style.setProperty('display', 'none', 'important');
    } else {
      if (faqSectionWrapper) faqSectionWrapper.style.removeProperty('display');
      if (faqSection) faqSection.style.removeProperty('display');
      if (faqFooter) faqFooter.style.removeProperty('display');
      if (siteFooter) siteFooter.style.removeProperty('display');
    }
  }

  setupStep2() {
    if (!this.step2Section) return;

    if (this.step2ClickHandler) {
      this.step2Section.removeEventListener('click', this.step2ClickHandler);
    }

    this.step2ClickHandler = (e) => {
      // Click on configurator title to go back to step 1
      if (e.target.closest('[data-configurator-title]')) {
        e.preventDefault();
        this.hideStep2();
        return;
      }

      const tab = e.target.closest('[data-tab-btn], .tab-btn');
      if (tab?.dataset.tab) {
        // Prevent navigation to disabled tabs (e.g., Summary when form not filled)
        if (tab.disabled || tab.classList.contains('disabled')) {
          e.preventDefault();
          return;
        }
        this.switchTab(tab.dataset.tab);
        return;
      }

      if (e.target.closest('[data-back-step]')) {
        e.preventDefault();
        this.goToPreviousTab();
        return;
      }

      if (e.target.closest('[data-next-step]')) {
        e.preventDefault();
        this.goToNextTab();
        return;
      }

      // Handle Save Design button click
      const saveDesignBtn = e.target.closest('[data-save-design]');
      if (saveDesignBtn) {
        e.preventDefault();
        
        // Handle Start Over button click
        if (saveDesignBtn?.dataset.startOver === 'true') {
          this.handleStartOver();
          return;
        }
        
        // Get current tab
        const currentTab = q('[data-tab-btn].active', '.tab-btn.active', this.step2Section)?.dataset.tab;
        
        // If already on services tab, try to submit the form
        if (currentTab === 'services') {
          if (!this.hasValidUserFormData()) {
            // Try to submit the form programmatically
            const form = $('[data-summary-form]', this.step2Section);
            if (form) {
              if (form.checkValidity()) {
                // Form is valid - submit it
                form.requestSubmit();
              } else {
                // Form is invalid - show validation messages and focus first invalid field
                form.reportValidity();
                this.focusSaveDesignForm();
              }
            } else {
              this.focusSaveDesignForm();
            }
          } else {
            // Form already submitted, go to summary
            this.switchTab('summary');
          }
          return;
        }
        
        // If not on services tab, redirect to services tab
        this.switchTab('services');
        
        // Scroll to form or installation options after a short delay to ensure content is rendered
        setTimeout(() => {
          const form = $('[data-summary-form]', this.step2Section);
          if (form && form.offsetParent !== null) {
            // Form is visible, focus on it
            this.focusSaveDesignForm();
          } else {
            // Form not visible yet (user needs to select installation first), focus on installation options
            this.focusInstallationOptions();
          }
        }, 150);
        
        return;
      }
    };

    this.step2Section.addEventListener('click', this.step2ClickHandler);
  }

  // ============================================================================
  // DATA LOADING & INITIALIZATION
  // ============================================================================

  loadStep2Data() {
    const modelConfigEl = $('[data-model-config]', this.step2Section);
    const variantFallbackEl = $('[data-variant-fallback]', this.step2Section);
    const sectionSettingsEl = $('[data-section-settings]', this.step2Section);

    let modelConfig = null, variantFallback = null;

    try {
      if (modelConfigEl?.textContent.trim()) {
        const rawData = modelConfigEl.textContent.trim();
        modelConfig = rawData.startsWith('"') && rawData.endsWith('"')
          ? JSON.parse(JSON.parse(rawData))
          : JSON.parse(rawData);
      }
    } catch (e) { console.error('Error parsing model config:', e); }

    try {
      if (variantFallbackEl?.textContent.trim()) {
        variantFallback = JSON.parse(variantFallbackEl.textContent);
      }
    } catch (e) { console.error('Error parsing variant fallback:', e); }

    try {
      if (sectionSettingsEl?.textContent.trim()) {
        this.sectionSettings = JSON.parse(sectionSettingsEl.textContent);
      }
    } catch (e) { console.error('Error parsing section settings:', e); }

    // Store model config
    this.modelConfig = {
      ...(modelConfig || {}),
      ...(this.sectionSettings || {}),
      services: {
        ...(modelConfig?.services || {}),
        ...(this.sectionSettings?.services || {})
      }
    };

    // Normalize steps from Signature format (steps[].sections[])
    this.steps = (modelConfig?.steps || []).map(step => ({
      step: step.step,
      id: step.id,
      name: step.title || step.id,
      description: step.description,
      sections: step.sections || []
    }));

    // Extract model data
    const modelData = modelConfig?.model || {};
    // Extract dimensions - support multiple JSON structures:
    // 1. Top-level: modelConfig.depth, modelConfig.length
    // 2. Nested: modelConfig.model.dimensions.width (-> depth), modelConfig.model.dimensions.depth (-> length)
    // Note: In some JSON formats, width=depth and depth=length (API naming convention)
    const dims = modelData.dimensions || {};
    const apiDepth = modelConfig?.depth || dims.width;
    const apiLength = modelConfig?.length || dims.depth;
    
    this.modelsData = {
      id: modelData.id,
      name: modelData.name,
      size: modelData.size,
      depth: apiDepth,
      length: apiLength,
      squareFootage: dims.squareFeet,
      basePrice: modelData.basePrice,
      description: modelData.description,
      domain: modelData.domain
    };

    // Extract default configuration
    this.defaultConfig = modelConfig?.defaultConfiguration || {};
    
    const variantInfo = variantFallback || {};
    this.basePrice = this.modelsData?.basePrice || variantInfo.price || 0;
    
    // Store variant documents for downloads section
    this.variantDocuments = variantFallback?.documents || [];
    
    // Re-initialize drawers on the new AJAX-loaded DOM
    this.setupPermitPlansDrawer();
    this.setupFoundationTypeDrawer();

    this.updateStep2Header(variantInfo);
    this.initializeDefaultConfig();
    this.generateTabs();
    // Disable Summary tab by default until form is submitted
    this.updateSummaryTabState();
    this.populateStepOptions(1);

    // Initialize 3D viewer if scene settings exist
    if (modelConfig?.loadingSequence?.length) {
      setTimeout(() => this.init3DViewer(), 100);
    }
  }

  initializeDefaultConfig() {
    this.currentConfig = { ...this.defaultConfig };
    this.currentStep = 1;
  }

  // ============================================================================
  // TABS & NAVIGATION
  // ============================================================================

  generateTabs() {
    const tabsContainer = $('[data-configurator-tabs]', this.step2Section);
    if (!tabsContainer || !this.steps.length) return;

    tabsContainer.innerHTML = '';

    this.steps.forEach((step, index) => {
      tabsContainer.appendChild(this.createTabButton(step.name, step.step, index === 0));
    });

    tabsContainer.appendChild(this.createTabButton('Services', this.steps.length + 1, false));
    tabsContainer.appendChild(this.createTabButton('Summary', this.steps.length + 2, false));
  }

  createTabButton(name, step, isActive) {
    const btn = document.createElement('button');
    btn.className = `tab-btn${isActive ? ' active' : ''}`;
    btn.setAttribute('data-tab-btn', '');
    btn.dataset.tab = name.toLowerCase().replace(/\s+/g, '-');
    btn.dataset.step = step;
    btn.textContent = name;
    return btn;
  }

  /**
   * Enable or disable the Summary tab based on form completion state.
   * Disabled tabs cannot be clicked and show visual feedback.
   */
  updateSummaryTabState() {
    const summaryTab = q('[data-tab-btn][data-tab="summary"]', '.tab-btn[data-tab="summary"]', this.step2Section);
    if (!summaryTab) return;

    const isEnabled = this.hasValidUserFormData();
    summaryTab.disabled = !isEnabled;
    summaryTab.classList.toggle('disabled', !isEnabled);
    summaryTab.setAttribute('aria-disabled', String(!isEnabled));
    
    if (!isEnabled) {
      summaryTab.title = 'Complete the installation form to view summary';
    } else {
      summaryTab.removeAttribute('title');
    }
  }

  switchTab(tabName) {
    const tabLower = tabName.toLowerCase().replace(/\s+/g, '-');
    const step = this.steps.find(s => (s.name || s.id).toLowerCase().replace(/\s+/g, '-') === tabLower);
    const stepNumber = step?.step;

    // Restore default 8-4 layout when NOT switching to summary (summary uses 6-6 layout)
    if (tabLower !== 'summary') {
      this.restoreDefaultLayout();
    }

    // Update tab buttons
    qa('[data-tab-btn]', '.tab-btn', this.step2Section).forEach(tab => {
      tab.classList.toggle('active', tab.dataset.tab === tabLower);
    });

    // Show/hide FAQ section and footer based on current tab
    this.toggleFaqSection(tabLower === 'summary');

    // Add/remove product-summary-tab class based on current tab (Signature-specific)
    const configuratorMain = $('[data-configurator-main]', this.step2Section);
    if (configuratorMain) {
      if (tabLower === 'summary') {
        configuratorMain.classList.add('product-summary-tab');
      } else {
        configuratorMain.classList.remove('product-summary-tab');
      }
    }

    // Reset footer visibility when not on summary
    if (tabLower !== 'summary') {
      const panelFooter = q('[data-panel-footer]', '.panel-footer', this.step2Section);
      if (panelFooter) panelFooter.style.display = '';

      const saveDesignBtn = $('[data-save-design]', this.step2Section);
      if (saveDesignBtn?.dataset.startOver === 'true') {
        saveDesignBtn.textContent = 'SAVE DESIGN';
        delete saveDesignBtn.dataset.startOver;
      }

      // Show header Save Design button when not on services form (will be hidden again in populateServicesOptions if needed)
      if (tabLower !== 'services' && saveDesignBtn) {
        saveDesignBtn.style.display = '';
      }

      // Pass skipQueueUpdate=true to prevent API calls on tab switch - API should only be called on Services step form submission
      this.updateTotalPrice(true);
    }

    // Route to appropriate content
    if (stepNumber) {
      this.populateStepOptions(stepNumber);
      this.update3DViewForStep(tabLower);
    } else if (tabLower === 'services') {
      this.populateServicesOptions();
      this.viewer3D?.setViewForStep(2);
    } else if (tabLower === 'summary') {
      this.showSummary();
      this.viewer3D?.setViewForStep(5);
    }

    // Scroll to top when switching tabs
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  update3DViewForStep(stepName) {
    if (!this.viewer3D) return;
    const isInteriorView = ['interior', 'choose-your-interior'].includes(stepName);
    
    if (isInteriorView) {
      this.viewer3D.setViewForStep(1); // Top-down interior view
    } else {
      this.viewer3D.setViewForStep(2); // Exterior view
      // For door/window step, start with front panel view (Signature-specific)
      if (stepName.includes('door') || stepName.includes('window')) {
        try {
          this.viewer3D.setViewForSection('frontPanel');
        } catch (e) {
          console.warn('Could not set front panel view:', e);
        }
      }
    }
  }

  goToNextTab() {
    const currentTab = q('[data-tab-btn].active', '.tab-btn.active', this.step2Section)?.dataset.tab;

    if (currentTab === 'services') {
      if (!this.hasValidUserFormData()) {
        // Try to submit the form programmatically when clicking SAVE DESIGN button
        const form = $('[data-summary-form]', this.step2Section);
        if (form) {
          if (form.checkValidity()) {
            // Form is valid - submit it
            form.requestSubmit();
          } else {
            // Form is invalid - show validation messages and focus first invalid field
            form.reportValidity();
            this.focusSaveDesignForm();
          }
        } else {
          this.focusSaveDesignForm();
        }
        return;
      }
      this.switchTab('summary');
      return;
    }

    const nextStep = this.steps.find(s => s.step === this.currentStep + 1);
    this.switchTab(nextStep?.name || 'services');
  }

  goToPreviousTab() {
    const currentTab = q('[data-tab-btn].active', '.tab-btn.active', this.step2Section)?.dataset.tab;

    if (currentTab === 'summary') {
      this.switchTab('services');
      return;
    }

    if (currentTab === 'services') {
      const lastStep = this.steps[this.steps.length - 1];
      if (lastStep) this.switchTab(lastStep.name);
      return;
    }

    if (this.currentStep > 1) {
      const prevStep = this.steps.find(s => s.step === this.currentStep - 1);
      if (prevStep) this.switchTab(prevStep.name);
    }
  }

  // ============================================================================
  // STEP OPTIONS RENDERING (Signature format: steps[].sections[])
  // ============================================================================

  populateStepOptions(stepNumber) {
    const step = this.steps.find(s => s.step === stepNumber);
    if (!step) return;

    const contentWrapper = $('[data-panel-content-wrapper]', this.step2Section);
    if (!contentWrapper) return;

    contentWrapper.innerHTML = '';

    const panelContent = document.createElement('div');
    panelContent.className = 'panel-content';
    panelContent.dataset.tabContent = (step.name || step.id).toLowerCase().replace(/\s+/g, '-');

    const sectionsContainer = document.createElement('div');
    sectionsContainer.className = 'config-sections-container';
    sectionsContainer.dataset.configSections = '';

    // Render sections for this step
    if (step.sections?.length) {
      step.sections.forEach(section => {
        // Skip sections with hide: true (unconditionally hidden)
        if (section.hide === true || section.hide === 'true') {
          return;
        }
        // Check showWhen conditions
        if (section.showWhen && !this.checkShowWhen(section.showWhen)) {
          return; // Skip hidden sections
        }
        if (section.hidden && !this.checkShowWhen(section.showWhen)) {
          return;
        }
        
        sectionsContainer.appendChild(this.createSectionElement(section));
      });
    }

    // Add Lifestyle interior package info AFTER config sections
    const stepNameLower = (step.name || step.id || '').toLowerCase();
    const isInteriorStep = stepNameLower.includes('interior');
    if (isInteriorStep && this.modelConfig?.interiorPackageInfo?.show && this.modelConfig.interiorPackageInfo.items) {
      const infoDiv = document.createElement('div');
      infoDiv.className = 'portland-interior-package-info';
      infoDiv.innerHTML = this.modelConfig.interiorPackageInfo.items;
      sectionsContainer.appendChild(infoDiv);
    }

    panelContent.appendChild(sectionsContainer);
    contentWrapper.appendChild(panelContent);
    this.updateNextButtonText(stepNumber);
    this.currentStep = stepNumber;
    // Pass skipQueueUpdate=true to prevent API calls on tab switch
    this.updateTotalPrice(true);
  }

  checkShowWhen(showWhen) {
    if (!showWhen) return true;
    
    for (const [key, allowedValues] of Object.entries(showWhen)) {
      const currentValue = this.currentConfig[key];
      if (Array.isArray(allowedValues)) {
        if (!allowedValues.includes(currentValue)) return false;
      } else {
        if (currentValue !== allowedValues) return false;
      }
    }
    return true;
  }

  createSectionElement(section) {
    // CHECK UNCONDITIONAL HIDE FIRST
    // If section has "hide": true, skip rendering entirely (no conditions)
    if (section.hide === true || section.hide === 'true') {
      return document.createDocumentFragment(); // Return empty fragment
    }
    
    // CHECK CONDITIONAL VISIBILITY
    // If section has "hidden": true and "showWhen" conditions, check if they're met
    if (section.hidden === true || section.hidden === 'true') {
      const conditionsMet = this.checkShowWhen(section.showWhen);
      if (!conditionsMet) {
        return document.createDocumentFragment(); // Return empty fragment
      }
    }
    
    // SPECIAL CASE: Hide sidingColor section when cedar siding is selected
    if (section.id === 'sidingColor') {
      const currentSiding = String(this.currentConfig?.siding || '').toLowerCase().trim();
      
      // More comprehensive cedar detection - check for cedar in any form
      const isCedarSiding = currentSiding.includes('cedar') || 
                           currentSiding === 'horiz_plank' || 
                           currentSiding === 'cedar-plank' || 
                           currentSiding === 'cedar-shake' ||
                           currentSiding === 'cedar_shake' ||
                           currentSiding === 'cedarplank' ||
                           currentSiding === 'cedarshake' ||
                           currentSiding.replace(/[\s_-]/g, '').includes('cedar');
      
      if (isCedarSiding) {
        return document.createDocumentFragment(); // Return empty fragment
      }
    }
    
    const sectionEl = document.createElement('div');
    sectionEl.className = 'config-section mb-6';
    sectionEl.dataset.configSection = section.id;
    
    // Store showWhen data for dynamic updates
    if (section.showWhen) {
      sectionEl.dataset.showWhen = JSON.stringify(section.showWhen);
    }
    if (section.hidden) {
      sectionEl.dataset.conditionalSection = 'true';
    }
    
    // Add click handler to rotate camera when section is focused (for panel sections)
    const isPanelSection = ['frontPanel', 'backPanel', 'leftPanel', 'rightPanel', 'roof'].includes(section.id);
    if (isPanelSection) {
      sectionEl.style.cursor = 'pointer';
      sectionEl.addEventListener('click', (e) => {
        // Only rotate if not clicking an option card
        if (!e.target.closest('[data-option-card]')) {
          if (this.viewer3D?.setViewForPanel) {
            this.viewer3D.setViewForPanel(section.id);
          }
        }
      });
    }

    const title = document.createElement('div');
    title.className = 'config-title d-flex align-items-center gap-1 mb-2';
    title.dataset.configTitle = section.id;
    
    const isPackageSection = section.options?.some(opt => {
      const name = String(opt.displayName || opt.value || '').toLowerCase().replace(/[\s_-]/g, '');
      return name.includes('shellonly') || name.includes('lifestyleinterior') || name.includes('fullyequip');
    });
    if (isPackageSection) {
      section.options.sort((a, b) => {
        const getScore = (opt) => {
          const name = String(opt.displayName || opt.value || '').toLowerCase().replace(/[\s_-]/g, '');
          if (name.includes('fullyequip')) return 1;
          if (name.includes('lifestyleinterior')) return 1;
          if (name.includes('shellonly')) return 2;
          return 3;
        };
        return getScore(a) - getScore(b);
      });
      // Force default assignment to the top-ranked package if not manually selected by user
      if (typeof this.hasForcedPackageDefault === 'undefined') {
        this.currentConfig[section.id] = section.options[0].value;
        this.hasForcedPackageDefault = true;
      }
    }

    const defaultValue = this.currentConfig[section.id];
    const selectedOption = section.options?.find(opt => opt.value === defaultValue) || section.options?.[0];
    
    // Set default if not set
    if (!defaultValue && selectedOption) {
      this.currentConfig[section.id] = selectedOption.value;
    }
    
    title.innerHTML = `<p class="config-title-label fs-sm fw-medium text-black mb-0" style="letter-spacing: -0.01em;">${section.title}:</p> <span class="config-title-value fs-sm" style="letter-spacing: -0.01em;">${selectedOption?.displayName || ''}</span>`;
    sectionEl.appendChild(title);

    if (section.description) {
      const desc = document.createElement('p');
      desc.className = 'config-section-desc d-none';
      desc.textContent = section.description;
      sectionEl.appendChild(desc);
    }
    
    // Add helpful hint for panel sections
    if (isPanelSection) {
      const hint = document.createElement('p');
      hint.className = 'config-section-hint d-none';
      hint.textContent = 'Click section title to rotate 3D view';
      sectionEl.appendChild(hint);
    }

    // Create options container
    const optionsContainer = this.createOptionsContainer(section);
    this.populateOptions(optionsContainer, section, defaultValue || selectedOption?.value);
    sectionEl.appendChild(optionsContainer);

    return sectionEl;
  }

  createOptionsContainer(section) {
    const container = document.createElement('div');
    const sectionId = section.id.toLowerCase();
    
    let className = 'config-options';
    
    // Determine container style based on section type
    if (section.options?.some(opt => opt.color?.hex)) {
      className += ' config-options--colors';
    } else if (section.options?.some(opt => opt.texture || opt.thumbnail)) {
      className += ' config-options--swatches';
    }

    container.className = className;
    container.dataset.configOptions = section.id;
    return container;
  }

  populateOptions(container, section, defaultValue) {
    if (!section.options?.length) return;

    // Filter out hidden options
    const visibleOptions = section.options.filter(opt => !opt.hide);
    
    visibleOptions.forEach((option, index) => {
      const isSelected = defaultValue ? option.value === defaultValue : index === 0;
      container.appendChild(this.createOptionCard(section.id, option, isSelected));
    });
  }

  createOptionCard(sectionId, option, isSelected) {
    const card = document.createElement('div');
    const isColor = option.color?.hex;
    const isTexture = option.thumbnail || option.texture;

    let classes = 'option-card';
    if (isSelected) classes += ' selected';
    if (isColor) classes += ' option-card--color';
    if (isTexture) classes += ' option-card--texture';

    card.className = classes;
    card.setAttribute('data-option-card', '');
    card.dataset.section = sectionId;
    card.dataset.value = option.value;
    card.dataset.price = option.price || 0;

    if (option.meshGroups) card.dataset.meshGroups = JSON.stringify(option.meshGroups);
    if (option.color?.hex) card.dataset.hex = option.color.hex;
    if (option.texture) card.dataset.texture = option.texture;
    if (option.modelFile) card.dataset.modelFile = option.modelFile;

    let html = `<span class="aspect-variant-check">
                  <svg viewBox="0 0 8 7" fill="none" style="width: 11px; height: 6px; margin-bottom: -1px;">
                    <path d="M0.5 3.35714L2.77027 5.5L7.5 0.5" stroke="currentColor" stroke-linecap="round"/>
                  </svg>
                </span>`;

    if (isColor) {
      html += `<div class="option-card-swatch w-100 h-100" style="background-color: ${option.color.hex}; transition: all .3s ease-in-out;"></div>`;
    } else if (isTexture) {
      html += `<div class="option-card-icon">
                <img src="${option.texture || option.thumbnail}" alt="${option.displayName}" title="${option.displayName}" loading="lazy" class="w-100 h-100 object-fit-contain">
              </div>`;
    }

    html += `<div class="option-card-content">
      <p class="option-card-label fw-medium mb-0 text-black">${option.displayName}</p>
      ${option.description ? `<p class="option-card-desc mb-0 fs-sm mt-1 text-gray-600 d-none">${option.description}</p>` : ''}
      ${option.price > 0
        ? `<span class="option-card-price text-gray-600 d-block">+${formatPrice(option.price)}</span>`
        : '<p class="option-card-included text-gray-600 mb-0">Included</p>'}
    </div>`;

    card.innerHTML = html;
    card.addEventListener('click', () => this.selectOption(sectionId, card, option));
    return card;
  }

  // ============================================================================
  // OPTION SELECTION
  // ============================================================================

  selectOption(sectionId, card, option) {
    // Update UI
    const container = card.parentElement;
    qa('[data-option-card]', '.option-card', container).forEach(c => c.classList.remove('selected'));
    card.classList.add('selected');

    // Update title
    const titleEl = $(`[data-config-title="${sectionId}"]`, this.step2Section);
    if (titleEl) {
      const valueEl = titleEl.querySelector('.config-title-value, [data-config-title-value]');
      if (valueEl) valueEl.textContent = option.displayName || option.value;
    }

    // Update config state
    this.currentConfig[sectionId] = option.value;

    // STATE RESET: If form was already submitted and user changes options,
    // invalidate the form data (requires re-submission before accessing Summary).
    // ⚠️ DO NOT call the installation API here - only clear form state.
    if (this.hasValidUserFormData()) {
      this.userFormData = null;
      window.signatureUserData = null;
      this.finalPriceData = null;
      this._finalPriceDataZip = null; // Clear cached ZIP so API is called again on next form submit
      // Disable Summary tab immediately
      this.updateSummaryTabState();
    }

    // DYNAMIC SIDING COLORS: When siding style changes, update the color options in UI
    if (sectionId === 'siding') {
      this.printAssociatedSidingColors(option);
      this.updateSidingColorOptions(option);
    }

    // Check if we need to show/hide dependent sections
    this.updateDependentSections();
    
    // After re-rendering, ensure sidingColor section visibility is correct for cedar
    // This is needed because updateDependentSections() may re-render the step
    if (sectionId === 'siding') {
      const colorSectionEl = $('[data-config-section="sidingColor"]', this.step2Section);
      
      if (colorSectionEl) {
        const sidingValue = String(option.value || this.currentConfig.siding || '').toLowerCase().trim();
        
        // More comprehensive cedar detection - check for cedar in any form
        const isCedarSiding = sidingValue.includes('cedar') || 
                             sidingValue === 'horiz_plank' || 
                             sidingValue === 'cedar-plank' || 
                             sidingValue === 'cedar-shake' ||
                             sidingValue === 'cedar_shake' ||
                             sidingValue === 'cedarplank' ||
                             sidingValue === 'cedarshake' ||
                             sidingValue.replace(/[\s_-]/g, '').includes('cedar');
        
        if (isCedarSiding) {
          colorSectionEl.style.display = 'none';
          delete this.currentConfig.sidingColor;
        } else {
          colorSectionEl.style.display = '';
        }
      }
    }

    // Update price display (skipQueueUpdate=true to prevent API call)
    this.updateTotalPrice(true);
    this.update3DViewer(sectionId, option);

    // Get meshGroups from section (not option)
    const sectionData = this.findSectionById(sectionId);
    const meshGroups = sectionData?.meshGroups || option.meshGroups || [];

    document.dispatchEvent(new CustomEvent('config:changed', {
      detail: { 
        section: sectionId, 
        value: option.value, 
        meshGroups: meshGroups,
        modelFile: option.modelFile,
        texture: option.texture,
        color: option.color,
        colorOptions: option.colorOptions, // Include colorOptions for siding styles
        config: this.currentConfig 
      }
    }));
  }

  /**
   * Print associated siding colors from JSON when siding style is selected
   */
  printAssociatedSidingColors(sidingStyleOption) {
    // Debug logging removed
  }

  /**
   * Update sidingColor section options dynamically based on selected siding style
   */
  updateSidingColorOptions(sidingStyleOption) {
    // Find the sidingColor section element first
    const colorSectionEl = $('[data-config-section="sidingColor"]', this.step2Section);
    if (!colorSectionEl) {
      return;
    }
    
    // Check if this is a cedar siding type (cedar doesn't have color options)
    const sidingValue = String(sidingStyleOption.value || '').toLowerCase().trim();
    
    // More comprehensive cedar detection
    const isCedarSiding = sidingValue.includes('cedar') || 
                        sidingValue === 'horiz_plank' || 
                        sidingValue === 'cedar-plank' || 
                        sidingValue === 'cedar-shake' ||
                        sidingValue === 'cedar_shake' ||
                        sidingValue === 'cedarplank' ||
                        sidingValue === 'cedarshake' ||
                        sidingValue.replace(/[\s_-]/g, '').includes('cedar');
    
    // Get color options - Priority: nested in style option > sidingColor section
    let colorOptions = sidingStyleOption.colorOptions;
    let colorSource = 'nested in siding style';
    
    // Fallback: Get from sidingColor section if not nested
    if (!colorOptions?.length) {
      const sidingColorSection = this.findSectionById('sidingColor');
      colorOptions = sidingColorSection?.options;
      colorSource = 'sidingColor section';
    }
    
    // Filter out hidden options
    if (colorOptions?.length) {
      colorOptions = colorOptions.filter(opt => !opt.hide);
    }
    
    // If cedar siding or no color options available, hide the entire section
    if (isCedarSiding || !colorOptions?.length) {
      colorSectionEl.style.display = 'none';
      
      // Clear the selected color from config since it's not applicable
      if (isCedarSiding) {
        delete this.currentConfig.sidingColor;
      }
      return;
    }
    
    // Show the section if it was hidden
    colorSectionEl.style.display = '';

    // Find the options container
    const optionsContainer = $('.config-options', colorSectionEl);
    if (!optionsContainer) {
      return;
    }

    // Clear existing options
    optionsContainer.innerHTML = '';

    // Check if current selected color is still available in new options
    const currentColor = this.currentConfig.sidingColor;
    const isCurrentColorAvailable = colorOptions.some(opt => opt.value === currentColor);
    
    // If current color not available, reset to first option (UI only, don't trigger 3D update)
    // The siding STYLE change will handle the 3D material update with the texture
    if (!isCurrentColorAvailable && colorOptions.length > 0) {
      this.currentConfig.sidingColor = colorOptions[0].value;
      // NOTE: We don't call updateConfiguration here to avoid overriding the texture
      // The texture from the siding STYLE is the primary visual
    }

    // Render new color options
    colorOptions.forEach((colorOption) => {
      const isSelected = colorOption.value === this.currentConfig.sidingColor;
      const card = this.createOptionCard('sidingColor', colorOption, isSelected);
      optionsContainer.appendChild(card);
    });

    // Update the section title with current selection
    const titleEl = $('[data-config-title="sidingColor"]', this.step2Section);
    if (titleEl) {
      const selectedOption = colorOptions.find(opt => opt.value === this.currentConfig.sidingColor);
      const valueEl = titleEl.querySelector('.config-title-value, [data-config-title-value]');
      if (valueEl && selectedOption) {
        valueEl.textContent = selectedOption.displayName || selectedOption.value;
      }
    }
  }

  updateDependentSections() {
    // Re-render current step to show/hide conditional sections
    const currentStepData = this.steps.find(s => s.step === this.currentStep);
    if (currentStepData) {
      this.populateStepOptions(this.currentStep);
    }
  }

  update3DViewer(sectionId, option) {
    if (!this.viewer3D) {
      console.log('[SignatureModal] ⚠️ update3DViewer - viewer3D not initialized');
      return;
    }

    // Support both option.color.hex (old) and option.hex (new) structures
    const hexColor = option.color?.hex || option.hex;

    // Get meshGroups from SECTION (not option) - meshGroups is defined at section level in JSON
    const sectionData = this.findSectionById(sectionId);
    const meshGroups = sectionData?.meshGroups || option.meshGroups || [];
    
    this.viewer3D.updateConfiguration({
      section: sectionId,
      value: option.value,
      meshGroups: meshGroups,
      modelFile: option.modelFile,
      hex: hexColor,
      texture: option.texture
    });
  }

  // Helper to find section data by sectionId
  findSectionById(sectionId) {
    if (!this.steps) return null;
    for (const step of this.steps) {
      if (step.sections) {
        const section = step.sections.find(s => s.id === sectionId);
        if (section) return section;
      }
    }
    return null;
  }

  // ============================================================================
  // 3D VIEWER
  // ============================================================================

  init3DViewer() {
    const viewerContainer = $('[data-model-viewer]', this.step2Section);
    if (!viewerContainer) return;

    const placeholder = viewerContainer.querySelector('[data-model-placeholder], .model-placeholder');
    if (placeholder) {
      // Use visibility/opacity to maintain layout stability on mobile (Signature-specific fix)
      placeholder.style.visibility = 'visible';
      placeholder.style.opacity = '1';
      placeholder.style.pointerEvents = 'auto';
      // Position placeholder absolutely to overlay without affecting layout
      placeholder.style.position = 'absolute';
      placeholder.style.top = '0';
      placeholder.style.left = '0';
      placeholder.style.right = '0';
      placeholder.style.bottom = '0';
      placeholder.style.zIndex = '10';
      // Hide the placeholder image during loading - only show spinner
      const placeholderImg = placeholder.querySelector('[data-placeholder-image], .placeholder-image');
      if (placeholderImg) placeholderImg.style.display = 'none';
      const loading = placeholder.querySelector('[data-loading-indicator], .loading-indicator');
      if (loading) loading.style.display = 'flex';
    }
    
    // Ensure the viewer container has relative/sticky positioning for absolute placeholder
    const currentPosition = getComputedStyle(viewerContainer).position;
    if (currentPosition !== 'sticky') {
      viewerContainer.style.position = 'relative';
    }

    if (this.viewer3D) {
      this.viewer3D.dispose();
      this.viewer3D = null;
    }

    const configData = this.buildConfigForViewer();
    if (!configData) {
      this.showPlaceholderOnError(placeholder);
      return;
    }

    // Helper to hide the 3D canvas during loading (Signature-specific fix)
    const hideCanvas = () => {
      const canvas = viewerContainer.querySelector('canvas');
      if (canvas) {
        canvas.style.opacity = '0';
        canvas.style.visibility = 'hidden';
      }
    };

    // Helper to show the 3D canvas after loading completes (Signature-specific fix)
    const showCanvas = () => {
      const canvas = viewerContainer.querySelector('canvas');
      if (canvas) {
        canvas.style.opacity = '1';
        canvas.style.visibility = 'visible';
        canvas.style.transition = 'opacity 0.3s ease-in-out';
      }
    };

    // Helper to show loading state without layout shift (Signature-specific mobile fix)
    const showLoadingState = () => {
      if (placeholder) {
        placeholder.style.visibility = 'visible';
        placeholder.style.opacity = '1';
        placeholder.style.pointerEvents = 'auto';
        const loading = placeholder.querySelector('[data-loading-indicator], .loading-indicator');
        if (loading) loading.style.display = 'flex';
        const img = placeholder.querySelector('[data-placeholder-image], .placeholder-image');
        if (img) img.style.display = 'none';
      }
    };

    // Helper to hide loading state without layout shift (Signature-specific mobile fix)
    const hideLoadingState = () => {
      if (placeholder) {
        placeholder.style.visibility = 'hidden';
        placeholder.style.opacity = '0';
        placeholder.style.pointerEvents = 'none';
        placeholder.style.transition = 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out';
        const loading = placeholder.querySelector('[data-loading-indicator], .loading-indicator');
        if (loading) loading.style.display = 'none';
        const img = placeholder.querySelector('[data-placeholder-image], .placeholder-image');
        if (img) img.style.display = 'none';
      }
    };

    this.waitFor3DModule().then(() => {
      const Configurator3D = window.SignatureConfigurator3D || window.ProductConfigurator3D;
      if (!Configurator3D) {
        this.showPlaceholderOnError(placeholder);
        return;
      }

      this.viewer3D = new Configurator3D(viewerContainer, configData);
      this.viewer3D.init();

      // Initially hide the canvas until model is loaded
      hideCanvas();

      viewerContainer.addEventListener('configurator:modelLoading', () => {
        // Hide canvas during loading to prevent visible model shift
        hideCanvas();
        // Show loading state without causing layout shift on mobile
        showLoadingState();
      });

      viewerContainer.addEventListener('configurator:modelLoaded', () => {
        // Apply configuration and set view BEFORE showing canvas
        this.applyConfigTo3D(this.currentConfig);
        this.setViewForCurrentStep();
        
        // Small delay to ensure model is fully positioned before revealing
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Hide loading state without layout shift, then show canvas
            hideLoadingState();
            showCanvas();
          });
        });
      });

      this.viewer3D.loadPanels(this.currentConfig).catch(() => this.showPlaceholderOnError(placeholder));
    }).catch(() => this.showPlaceholderOnError(placeholder));
  }

  buildConfigForViewer() {
    if (!this.modelConfig) return null;

    return {
      model: this.modelConfig.model || this.modelsData,
      steps: this.steps,
      defaultConfiguration: this.defaultConfig,
      loadingSequence: this.modelConfig.loadingSequence || [],
      materialGroups: this.modelConfig.materialGroups || {},
      sceneSettings: this.modelConfig.sceneSettings || {}
    };
  }

  applyConfigTo3D(config) {
    if (!this.viewer3D || !config) return;

    Object.entries(config).forEach(([key, value]) => {
      if (!value) return;
      
      // Find the option data
      for (const step of this.steps) {
        const section = step.sections?.find(s => s.id === key);
        if (section) {
          const option = section.options?.find(o => o.value === value);
          if (option) {
            this.viewer3D.updateConfiguration({
              section: key,
              value,
              meshGroups: option.meshGroups,
              modelFile: option.modelFile,
              hex: option.color?.hex,
              texture: option.texture,
              skipFocus: true
            });
          }
          break;
        }
      }
    });
  }

  setViewForCurrentStep() {
    if (!this.viewer3D) return;
    const currentStepData = this.steps.find(s => s.step === this.currentStep);
    const stepId = currentStepData?.id?.toLowerCase() || '';
    const isInterior = stepId === 'interior';
    this.viewer3D.setViewForStep(isInterior ? 1 : 2);
  }

  showPlaceholderOnError(placeholder) {
    if (!placeholder) return;
    // Show placeholder image only on error as fallback (Signature-specific fix)
    placeholder.style.visibility = 'visible';
    placeholder.style.opacity = '1';
    placeholder.style.pointerEvents = 'auto';
    const img = placeholder.querySelector('[data-placeholder-image], .placeholder-image');
    if (img) img.style.display = 'block';
    const loading = placeholder.querySelector('[data-loading-indicator], .loading-indicator');
    if (loading) loading.style.display = 'none';
  }

  waitFor3DModule(timeout = 10000) {
    return new Promise((resolve, reject) => {
      if (window.SignatureConfigurator3D || window.ProductConfigurator3D) {
        resolve();
        return;
      }
      const startTime = Date.now();
      const checkInterval = setInterval(() => {
        if (window.SignatureConfigurator3D || window.ProductConfigurator3D) {
          clearInterval(checkInterval);
          resolve();
        } else if (Date.now() - startTime > timeout) {
          clearInterval(checkInterval);
          reject(new Error('Timeout waiting for 3D module'));
        }
      }, 100);
    });
  }

  // ============================================================================
  // HEADER & PRICING
  // ============================================================================

  updateStep2Header(variantInfo = {}) {
    const titleEl = $('[data-configurator-title]', this.step2Section);
    const specsEl = $('[data-configurator-specs]', this.step2Section);
    // Get ALL price elements (header and mobile footer)
    const priceEls = $$('[data-configurator-price]', this.step2Section);

    const modelName = this.modelsData?.name || variantInfo.title || '';
    const size = this.modelsData?.size || '';
    const sqFt = this.modelsData?.squareFootage;

    if (titleEl) titleEl.textContent = modelName;
    if (specsEl) {
      const specs = [size, sqFt && `${sqFt} sq ft`].filter(Boolean);
      specsEl.textContent = specs.join(' | ');
    }

    this.basePrice = this.modelsData?.basePrice || variantInfo.price || 0;
    // Update ALL price elements
    priceEls.forEach(el => el.textContent = formatPrice(this.basePrice));
  }

  updateNextButtonText(currentStepNumber) {
    const nextBtnText = $('[data-next-step-text]', this.step2Section);
    if (nextBtnText) {
      const nextStep = this.steps.find(s => s.step === currentStepNumber + 1);
      nextBtnText.textContent = nextStep ? (nextStep.name || '') : 'Services';
    }
    
    const backBtn = $('[data-back-step]', this.step2Section);
    if (backBtn) {
      backBtn.style.display = currentStepNumber === 1 ? 'none' : '';
    }
  }

  updateTotalPrice(skipQueueUpdate = false) {
    // Prevent infinite loops by allowing callers to skip the queue update
    if (this._updatingPrice) return; // Guard against recursive calls
    this._updatingPrice = true;

    try {
      let additionalPrice = 0;

      // Calculate price from all selected options
      this.steps.forEach(step => {
        if (!step.sections) return;
        step.sections.forEach(section => {
          if (section.id === 'installation') return;
          
          const configValue = this.currentConfig[section.id];
          if (!configValue) return;
          const selectedOption = section.options?.find(opt => opt.value === configValue);
          if (selectedOption?.price) additionalPrice += selectedOption.price;
        });
      });

      // Base product option price (excludes shipping/installation/permit)
      this.configPrice = additionalPrice;

      // Add installation + shipping costs from API (only after ZIP is known)
      let servicesPrice = 0;
      if (this.hasValidUserFormData()) {
        const apiShipping = normalizeNumber(this.finalPriceData?.shipping_cost, 0);
        const apiInstall = window.ConfiguratorShared.computeInstallationCost(
          this.finalPriceData?.breakdown?.installation,
          this.currentConfig?.installation
        );
        servicesPrice = apiShipping + apiInstall;
      }

      const totalPrice = this.basePrice + additionalPrice + servicesPrice + (this.permitPlanPrice || 0);

      // Update ALL price elements (header and mobile footer)
      const priceEls = $$('[data-configurator-price]', this.step2Section);
      priceEls.forEach(el => el.textContent = formatPrice(totalPrice));

      // Update ALL monthly price elements
      const monthlyEls = $$('[data-monthly-price]', this.step2Section);
      monthlyEls.forEach(el => el.textContent = formatPrice(Math.round(totalPrice / 180)));

      // Only queue update if explicitly requested and not already in progress
      // This prevents infinite loops - queueFinalPriceUpdate will call updateTotalPrice with skipQueueUpdate=true
      if (!skipQueueUpdate && this.hasValidUserFormData() && !this._priceUpdateQueued) {
        this._priceUpdateQueued = true;
        // Use requestAnimationFrame to defer the API call and prevent blocking
        requestAnimationFrame(() => {
          this._priceUpdateQueued = false;
          this.queueFinalPriceUpdate();
        });
      }
    } finally {
      this._updatingPrice = false;
    }
  }

  // ============================================================================
  // SERVICES TAB
  // ============================================================================

  hasValidUserFormData() {
    return Boolean(
      this.userFormData &&
      this.userFormData.firstName &&
      this.userFormData.email &&
      this.userFormData.zipCode
    );
  }

  getModelSlug() {
    const modelName = this.modelsData?.name || this.modelConfig?.model?.name || this.modelConfig?.name || this.modelConfig?.model || 'signature';
    return window.ConfiguratorShared.canonicalModel(modelName);
  }

  buildFinalPricePayload(installationTypeOverride) {
    const zip = String(this.userFormData?.zipCode || '').trim();

    const pick = (...keys) => window.ConfiguratorShared.pickFirst(this.currentConfig, keys);

    const depth = normalizeNumber(this.modelsData?.depth ?? this.modelConfig?.depth ?? this.sectionSettings?.depth ?? this.modelConfig?.model?.dimensions?.depth, null);
    const length = normalizeNumber(this.modelsData?.length ?? this.modelConfig?.length ?? this.sectionSettings?.length ?? this.modelConfig?.model?.dimensions?.length, null);

    return window.ConfiguratorShared.buildInstallationCostPayload({
      zip,
      model: this.getModelSlug(),
      depth,
      length,
      interior: pick('interior', 'interiorFinish', 'mainFloorFinish', 'flooring'),
      kitchen: pick('kitchen') || '',
      roofStyle: pick('roofStyle', 'roof', 'roofType'),
    });
  }

  async fetchFinalPrice(payload) {
    // DRY: moved to `assets/configurator-shared.js` installation cost client.
    return payload;
  }

  queueFinalPriceUpdate(servicesContainer) {
    if (!this.hasValidUserFormData()) return;

    if (!this.installationCostClient) {
      this.installationCostClient = window.ConfiguratorShared.createInstallationCostClient();
    }

    const selectedType = String(this.currentConfig.installation || 'shell');
    const depth = normalizeNumber(this.modelsData?.depth ?? this.modelConfig?.depth ?? this.sectionSettings?.depth ?? this.modelConfig?.model?.dimensions?.depth, null);
    const length = normalizeNumber(this.modelsData?.length ?? this.modelConfig?.length ?? this.sectionSettings?.length ?? this.modelConfig?.model?.dimensions?.length, null);

    window.ConfiguratorShared.queueInstallationPricing({
      client: this.installationCostClient,
      zip: this.userFormData?.zipCode,
      modelRaw: this.getModelSlug(),
      depth,
      length,
      currentConfig: this.currentConfig,
      selectedType
    })
      .then((out) => {
        if (!out?.installation) return;
        this.finalPriceData = out.finalPriceData;

        window.ConfiguratorShared.applyInstallationCostsToServiceOptions({
          container: servicesContainer || this.step2Section,
          installation: out.installation,
          formatPrice,
          prefixPlus: false,
        });

        // Pass skipQueueUpdate=true to prevent infinite loop
        this.updateTotalPrice(true);

        // Notify only when we actually fetched new pricing (avoid spam on cached recalcs).
        if (out?.meta?.didFetch && out?.meta?.payloadKey) {
          if (this._lastInstallPricingToastKey !== out.meta.payloadKey) {
            this._lastInstallPricingToastKey = out.meta.payloadKey;
            window.ConfiguratorShared.showSuccess('Installation pricing updated.', 'Pricing');
          }
        }
      })
      .catch((e) => {
        const msg = e?.message || String(e || 'Installation pricing request failed');
        window.ConfiguratorShared.showError(msg, 'Installation pricing');
      });
  }

  applyFinalPriceToServicesUI(container) {
    if (!container || !this.finalPriceData) return;

    const install = this.finalPriceData?.breakdown?.installation;
    if (!install) return;

    window.ConfiguratorShared.applyInstallationCostsToServiceOptions({
      container,
      installation: install,
      formatPrice,
      prefixPlus: false,
    });
  }

  focusSaveDesignForm() {
    const contentWrapper = $('[data-panel-content-wrapper]', this.step2Section);
    if (!contentWrapper) return;
    const firstInput =
      contentWrapper.querySelector('[data-summary-form] input[name="firstName"]') ||
      contentWrapper.querySelector('[data-summary-form] input') ||
      null;
    if (firstInput) {
      firstInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      firstInput.focus({ preventScroll: true });
    }
  }

  focusInstallationOptions() {
    const contentWrapper = $('[data-panel-content-wrapper]', this.step2Section);
    if (!contentWrapper) return;
    const installSection =
      contentWrapper.querySelector('[data-config-section="installation"]') ||
      contentWrapper.querySelector('[data-services-options]') ||
      null;
    if (installSection) installSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  getSaveDesignFormHTML() {
    return `
      <div class="config-section summary-form-view" data-save-design-form>
        <div class="text-center mb-md-5 mb-3 pe-xl-1">
          <h2 class="h3 mb-md-3 mb-2">You're almost done!</h2>
          <p class="mb-0">Your build is ready. Save your design by completing the form and get access to information about pricing, shipping, professional assembly, local permits, and financing options.</p>
        </div>
        <form class="summary-contact-form" data-summary-form>
          <div class="input-group mb-2"><input type="text" name="firstName" placeholder="First Name *" class="form-control" required></div>
          <div class="input-group mb-2"><input type="text" name="lastName" placeholder="Last Name *" class="form-control" required></div>
          <div class="input-group mb-2"><input type="email" name="email" placeholder="Email *" class="form-control" required></div>
          <div class="input-group mb-2"><input type="text" name="zipCode" placeholder="ZIP code *" class="form-control" required></div>
          <div class="input-group mb-2"><input type="tel" name="phone" placeholder="Phone Number" class="form-control"></div>
          <p class="form-disclaimer mb-xl-13 mb-md-7 mb-4">By providing a telephone number and when scheduling your consultation you are consenting to be contacted by SMS text message. Message & data rates may apply. You can reply STOP to opt-out of further messaging.</p>
          <button type="submit" class="visually-hidden" aria-hidden="true" tabindex="-1">Submit</button>
        </form>
      </div>
    `;
  }

  attachSaveDesignFormHandlers(container) {
    const form = $('[data-summary-form]', container);
    if (!form) return;

    // Create and attach form validator for real-time validation
    const validator = window.ConfiguratorShared?.createFormValidator?.(form);
    if (validator) {
      validator.attachListeners();
      this._formValidator = validator;
    }

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      
      // Validate form using our custom validator first
      if (this._formValidator) {
        const { valid, firstInvalid } = this._formValidator.validateAll();
        if (!valid) {
          firstInvalid?.focus();
          firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
      }
      
      // Prevent double submission
      if (this._isValidatingZip) return;
      
      const formData = new FormData(form);
      const pendingUserData = {
        firstName: String(formData.get('firstName') || ''),
        lastName: String(formData.get('lastName') || ''),
        email: String(formData.get('email') || ''),
        zipCode: String(formData.get('zipCode') || ''),
        phone: String(formData.get('phone') || '')
      };

      // Show loading state on submit button
      const submitBtn = form.querySelector('[data-next-step], button[type="submit"], .next-step-btn');
      const footerBtn = $('[data-next-step]', this.step2Section);
      const btnToDisable = submitBtn || footerBtn;
      const nextBtnTextSpan = $('[data-next-step-text]', this.step2Section);
      const originalBtnText = nextBtnTextSpan?.textContent || 'SAVE DESIGN';
      
      try {
        this._isValidatingZip = true;
        if (btnToDisable) {
          btnToDisable.disabled = true;
        }
        // Show "Loading..." text on button (similar to Design Your Own button)
        if (nextBtnTextSpan) {
          nextBtnTextSpan.textContent = 'Loading...';
        }

        // Build payload for validation
        const depth = normalizeNumber(this.modelsData?.depth ?? this.modelConfig?.depth ?? this.modelConfig?.model?.dimensions?.depth, null);
        const length = normalizeNumber(this.modelsData?.length ?? this.modelConfig?.length ?? this.modelConfig?.model?.dimensions?.length, null);
        const pick = (...keys) => window.ConfiguratorShared.pickFirst(this.currentConfig, keys);

        // CRITICAL: Validate ZIP code with API BEFORE allowing step progression
        const result = await window.ConfiguratorShared.validateZipCodeWithAPI({
          zip: pendingUserData.zipCode,
          model: this.getModelSlug(),
          depth,
          length,
          interior: pick('interior', 'interiorFinish', 'mainFloorFinish', 'flooring'),
          kitchen: pick('kitchen') || '',
          roofStyle: pick('roofStyle', 'roof', 'roofType'),
          base_product_price: this.basePrice + this.configPrice,
        });

        // API returned 200 and valid data - NOW we can proceed
        this.userFormData = pendingUserData;
        window.signatureUserData = this.userFormData;
        
        // Store the validated API data (new format from final-price API)
        if (result?.data) {
          const apiData = result.data;
          const breakdownInstallation = apiData.breakdown?.installation || {};
          const breakdownShipping = apiData.breakdown?.shipping || {};
          const selectedType = String(this.currentConfig.installation || 'shell');
          
          // Build installation object with prices from breakdown
          const installationWithPrices = {
            shell: normalizeNumber(breakdownInstallation.shell, 0),
            lifestyle: normalizeNumber(breakdownInstallation.lifestyle, 0),
            kitchenbath: normalizeNumber(breakdownInstallation.kitchenbath, 0),
            selected_type: breakdownInstallation.selected_type || selectedType,
            selected_cost: normalizeNumber(breakdownInstallation.selected_cost || apiData.installation_cost, 0),
            state_code: breakdownInstallation.state_code,
            zone: breakdownInstallation.zone,
          };
          
          this.finalPriceData = {
            breakdown: { 
              installation: installationWithPrices,
              installationPrices: {
                shell: normalizeNumber(breakdownInstallation.shell, 0),
                lifestyle: normalizeNumber(breakdownInstallation.lifestyle, 0),
                kitchenbath: normalizeNumber(breakdownInstallation.kitchenbath, 0),
              },
              shipping: {
                base_subtotal: normalizeNumber(breakdownShipping.base_subtotal, 0),
                depth_surcharge: normalizeNumber(breakdownShipping.depth_surcharge, 0),
                interior_surcharge: normalizeNumber(breakdownShipping.interior_surcharge, 0),
                inflation_surcharge: normalizeNumber(breakdownShipping.inflation_surcharge, 0),
              },
            },
            installation_cost: normalizeNumber(apiData.installation_cost, 0),
            shipping_cost: normalizeNumber(apiData.shipping_cost, 0),
            total_price: normalizeNumber(apiData.total_price, 0),
            product_price: normalizeNumber(apiData.product_price, 0),
            city: apiData.city || '',
            state_code: apiData.state_code || '',
          };
          // Track the ZIP code used to fetch this data to prevent duplicate API calls
          this._finalPriceDataZip = pendingUserData.zipCode;
        }

        // Call Save Design API (non-blocking - don't wait for it)
        // This runs in parallel with the UI update
        window.ConfiguratorShared.saveDesignToAPI(this, pendingUserData)
          .then((saveResult) => {
            if (saveResult?.success) {
              console.log('[Signature] Design saved successfully:', saveResult.design_id);
            } else if (saveResult?.error) {
              console.warn('[Signature] Save Design API:', saveResult.error);
            }
          })
          .catch((error) => {
            // Log error but don't block user flow
            console.error('[Signature] Save Design API error:', error);
          });

        // SUCCESS: Show installation options (pass true to show options instead of form)
        this.populateServicesOptions(true);
        
        setTimeout(() => {
          this.focusInstallationOptions();
          // Update total price after switching tabs
          this.updateTotalPrice(true);
        }, 50);
        
      } catch (error) {
        // FAILURE: Stay on current step and show error
        const errorMessage = error?.message || 'ZIP code validation failed. Please try again.';
        window.ConfiguratorShared.showError(errorMessage, 'Invalid ZIP Code');
        
        // Show contact link below the Save Design button
        window.ConfiguratorShared.showContactLinkOnError(this.step2Section);
        
        // Focus the ZIP code input for correction
        const zipInput = form.querySelector('input[name="zipCode"]');
        if (zipInput) {
          zipInput.focus();
          zipInput.select();
        }
        
        // Do NOT switch tabs - stay on current step
        // Restore button text only on error (success case will have already switched to installation options with SUMMARY)
        if (btnToDisable) {
          btnToDisable.disabled = false;
        }
        // Also re-enable the footer button if it's different from btnToDisable
        if (footerBtn && footerBtn !== btnToDisable) {
          footerBtn.disabled = false;
        }
        // Also re-enable submit button if it's different
        if (submitBtn && submitBtn !== btnToDisable) {
          submitBtn.disabled = false;
        }
        // Restore the span text, not the entire button (to preserve button structure with arrow)
        if (nextBtnTextSpan) {
          nextBtnTextSpan.textContent = originalBtnText;
        }
      } finally {
        this._isValidatingZip = false;
        // Re-enable ALL buttons that might have been disabled
        if (btnToDisable) {
          btnToDisable.disabled = false;
        }
        if (footerBtn && footerBtn !== btnToDisable) {
          footerBtn.disabled = false;
        }
        if (submitBtn && submitBtn !== btnToDisable) {
          submitBtn.disabled = false;
        }
      }
    });
  }

  populateServicesOptions(showInstallationOptions = false) {
    const contentWrapper = $('[data-panel-content-wrapper]', this.step2Section);
    if (!contentWrapper) return;

    contentWrapper.innerHTML = '';

    const installationOptions = this.modelConfig?.services?.installation?.options || 
                               this.sectionSettings?.services?.installation?.options || [];

    const panelContent = document.createElement('div');
    panelContent.className = 'panel-content';
    panelContent.dataset.tabContent = 'services';

    // Only show installation options if explicitly requested (after form submission)
    // Otherwise always show the form
    if (showInstallationOptions || this.hasValidUserFormData()) {
      console.log('[Signature] populateServicesOptions - Showing installation options');
      this.showInstallationOptionsContent(contentWrapper, panelContent, installationOptions);
      return;
    }

    // Always show the form when user navigates to Services step
    console.log('[Signature] populateServicesOptions - Showing form');
    
    // Show the form (don't clear user data - just show the form)
    panelContent.innerHTML = this.getSaveDesignFormHTML();
    contentWrapper.appendChild(panelContent);
    this.attachSaveDesignFormHandlers(panelContent);

    const nextBtnText = $('[data-next-step-text]', this.step2Section);
    if (nextBtnText) nextBtnText.textContent = 'SAVE DESIGN';
    const backBtn = $('[data-back-step]', this.step2Section);
    if (backBtn) backBtn.style.display = '';
    
    // Disable Summary tab when form is shown (not yet valid)
    this.updateSummaryTabState();

    // Hide header Save Design button when form is shown
    const headerSaveBtn = $('[data-save-design]', this.step2Section);
    if (headerSaveBtn) headerSaveBtn.style.display = 'none';

    this.updateTotalPrice(true);
  }

  showInstallationOptionsContent(contentWrapper, panelContent, installationOptions) {
    installationOptions = [...installationOptions].sort((a, b) => {
      const valA = (a.value || '').toLowerCase();
      const valB = (b.value || '').toLowerCase();
      
      const getOrder = (val) => {
        if (val.includes('lifestyle')) return 1;
        if (val === 'shell' || val.includes('shell')) return 2;
        if (val.includes('product') || val.includes('diy') || val === 'none') return 3;
        return 99;
      };
      
      return getOrder(valA) - getOrder(valB);
    });

    const rawInteriorValue = String(
      this.currentConfig.interiorOptions || 
      this.currentConfig.interiorPackage || 
      this.currentConfig.layout || 
      this.currentConfig.interior ||
      window.ConfiguratorShared?.pickFirst?.(this.currentConfig, ['interiorFinish', 'mainFloorFinish', 'flooring']) || 
      ''
    ).toLowerCase().replace(/[\s_-]/g, '');

    const isShellOnly = rawInteriorValue.includes('shellonly') || rawInteriorValue === 'shell' || rawInteriorValue === 'none';

    if (isShellOnly) {
      installationOptions = installationOptions.filter(o => !/lifestyle/i.test(o.value));
    }

    let defaultVal;
    if (isShellOnly) {
      // For shell-only interiors, look for "shell-only" or "shell" installation options
      defaultVal = installationOptions.find(o => o.value === 'shell-only' || o.value === 'shell')?.value;
    } else {
      // For lifestyle interiors, look for "lifestyle" options
      defaultVal = installationOptions.find(o => /lifestyle/i.test(o.value))?.value;
    }
    
    if (!defaultVal) {
      defaultVal = installationOptions.find(o => o.isDefault)?.value || installationOptions[0]?.value;
    }

    const currentValue = this.currentConfig.installation || defaultVal;

    if (currentValue) this.currentConfig.installation = currentValue;

    panelContent.innerHTML = `
      <div class="config-section services-section" data-config-section="installation">
        <p class="services-header fw-medium mb-1" data-services-header>Installation</p>
        <div class="services-options" data-services-options>
          ${installationOptions.map(option => this.renderServiceOption(option, option.value === currentValue)).join('')}
        </div>
      </div>
    `;

    contentWrapper.appendChild(panelContent);

    qa('[data-service-option]', '.service-option-card', panelContent).forEach(card => {
      card.addEventListener('click', () => {
        const { section, value } = card.dataset;
        
        qa('[data-service-option]', '.service-option-card', card.closest('[data-services-options], .services-options')).forEach(c => c.classList.remove('selected'));
        card.classList.add('selected');
        this.currentConfig[section] = value;
        
        this.updateTotalPrice(true);
        
        if (typeof this.recalculateSummaryTotal === 'function') {
          this.recalculateSummaryTotal();
        }
      });
    });

    this.currentStep = this.steps.length + 1;
    const nextBtnText = $('[data-next-step-text]', this.step2Section);
    if (nextBtnText) nextBtnText.textContent = 'Summary';
    
    const backBtn = $('[data-back-step]', this.step2Section);
    if (backBtn) backBtn.style.display = '';

    const headerSaveBtn = $('[data-save-design]', this.step2Section);
    if (headerSaveBtn) {
      headerSaveBtn.style.display = '';
      headerSaveBtn.textContent = 'START OVER';
      headerSaveBtn.dataset.startOver = 'true';
    }
    
    // Pass skipQueueUpdate=true to prevent another API call - we already have the data
    this.updateTotalPrice(true);
    this.updateSummaryTabState();

    // Apply the installation prices from the API response
    const installation = this.finalPriceData?.breakdown?.installation;
    if (installation) {
      window.ConfiguratorShared.applyInstallationCostsToServiceOptions({
        container: panelContent,
        installation: installation,
        formatPrice: (val) => window.ConfiguratorShared.formatPriceSmart(val, { noDecimals: true }),
        prefixPlus: false,
      });
    }
  }

  renderServiceOption(option, isSelected) {
    const priceDisplay = option.price > 0 ? `<span data-service-price style="letter-spacing: -0.02em;">${formatPrice(option.price)}</span>` : '';
    const noteHTML = option.note ? `<p class="fs-sm fw-medium mt-4 mb-0" data-service-note>${option.note}</p>` : '';
    return `
      <div class="service-option-card${isSelected ? ' selected' : ''}" data-service-option data-section="installation" data-value="${option.value}" data-display-name="${option.displayName}" data-price="${option.price || 0}">
        <div class="service-option-header d-flex align-items-center justify-content-between mb-2" data-service-header>
          <p class="service-option-title fw-medium mb-0" data-service-title style="letter-spacing: -0.02em;">${option.displayName}</p>
          ${priceDisplay}
        </div>
        <span class="aspect-variant-check" data-service-checkmark>
          <svg viewBox="0 0 8 7" fill="none" style="width: 11px; height: 6px; margin-bottom: -1px;">
            <path d="M0.5 3.35714L2.77027 5.5L7.5 0.5" stroke="currentColor" stroke-linecap="round"></path>
          </svg>
        </span>
        <p class="fs-sm mb-0" data-service-description style="letter-spacing: -0.01em;">${option.description}</p>
        ${noteHTML}
      </div>
    `;
  }

  // ============================================================================
  // SUMMARY TAB
  // ============================================================================

  showSummary() {
    // Summary tab should be disabled when form is invalid.
    // Do NOT redirect - just prevent display if somehow accessed.
    if (!this.hasValidUserFormData()) {
      // Ensure Summary tab is disabled
      this.updateSummaryTabState();
      return;
    }

    this.showThankYouSummary();

    // Add product-summary-tab class to data-configurator-main element (Signature-specific)
    const configuratorMain = $('[data-configurator-main]', this.step2Section);
    if (configuratorMain) {
      configuratorMain.classList.add('product-summary-tab');
    }

    this.currentStep = this.steps.length + 2;

    const panelFooter = q('[data-panel-footer]', '.panel-footer', this.step2Section);
    if (panelFooter) panelFooter.style.display = 'none';

    const saveDesignBtn = $('[data-save-design]', this.step2Section);
    if (saveDesignBtn) {
      saveDesignBtn.textContent = 'START OVER';
      saveDesignBtn.dataset.startOver = 'true';
    }
  }

  showThankYouSummary() {
    const contentWrapper = $('[data-panel-content-wrapper]', this.step2Section);
    if (!contentWrapper) return;

    // Change layout to 6-6 columns on thank you page
    const viewer = $('.configurator-viewer', this.step2Section);
    const panel = $('.configurator-panel', this.step2Section);
    if (viewer) {
      viewer.classList.remove('col-lg-8');
      viewer.classList.add('col-lg-6');
      // Center the model viewer content for col-6 layout
      const modelContainer = $('[data-model-viewer], .model-viewer-container', viewer);
      if (modelContainer) {
        modelContainer.style.setProperty('max-width', '100%', 'important');
        modelContainer.style.setProperty('margin', '0 auto', 'important');
      }
    }
    if (panel) {
      panel.classList.remove('col-lg-4');
      panel.classList.add('col-lg-6');
    }

    // Trigger 3D viewer resize after layout change
    setTimeout(() => {
      if (this.viewer3D?.sceneManager?.onResize) {
        this.viewer3D.sceneManager.onResize();
      }
    }, 100);

    contentWrapper.innerHTML = '';

    const panelContent = document.createElement('div');
    panelContent.className = 'panel-content summary-panel';
    panelContent.dataset.tabContent = 'summary';
    panelContent.innerHTML = this.buildThankYouHTML();
    contentWrapper.appendChild(panelContent);

    this.setupThankYouEventListeners(panelContent);
    // FAQ section removed - no longer shown on summary page
  }

  buildThankYouHTML() {
    const variantTitle = this.modelsData?.name || '';
    const productPrice = this.basePrice + this.configPrice;
    
    const installationOptions = this.modelConfig?.services?.installation?.options ||
                               this.sectionSettings?.services?.installation?.options || [];
    const selectedInstallation = installationOptions.find(opt => opt.value === this.currentConfig.installation);
    
    // Get installation price from API breakdown
    // For "shell-lifestyle" option, use lifestyle price from API breakdown
    let installationPrice = 0;
    const installationValue = this.currentConfig.installation || '';
    
    if (this.finalPriceData?.breakdown?.installation) {
      const breakdown = this.finalPriceData.breakdown.installation;
      
      // If user selected "shell-lifestyle", use lifestyle price from API breakdown
      if (installationValue.toLowerCase().includes('lifestyle')) {
        installationPrice = normalizeNumber(breakdown.lifestyle, 0);
      } else {
        // For other options, use the computed installation cost
        const computedCents = window.ConfiguratorShared.computeInstallationCost(
          breakdown,
          installationValue
        );
        installationPrice = computedCents > 0 ? computedCents : normalizeNumber(selectedInstallation?.price, 0);
      }
    } else {
      // Fallback if no API data available
      installationPrice = normalizeNumber(selectedInstallation?.price, 0);
    }
    
    const installationName = selectedInstallation?.displayName || '';
    
    const shippingPrice = normalizeNumber(this.finalPriceData?.shipping_cost, this.sectionSettings?.shippingPrice || this.modelConfig?.shippingPrice || 0);
    const estimatedTotal = productPrice + shippingPrice + this.permitPlanPrice + installationPrice;
    const zipCode = this.userFormData?.zipCode || '';
    const configSummary = this.buildConfigSummaryString();

    // Build product title
    const productTitle = variantTitle ? `${variantTitle} ${this.modelsData?.size || ''}`.trim() : '';

    // Build shipping label with city and state_code from API
    const city = this.finalPriceData?.city || '';
    const stateCode = this.finalPriceData?.state_code || '';
    const shippingLocation = this.sectionSettings?.shippingLocation || this.modelConfig?.shippingLocation || '';
    
    let shippingLabel = 'Shipping';
    if (city && stateCode && zipCode) {
      shippingLabel = `Shipping to ${city}, ${stateCode} (${zipCode})`;
    } else if (city && stateCode) {
      shippingLabel = `Shipping to ${city}, ${stateCode}`;
    } else if (shippingLocation && zipCode) {
      shippingLabel = `Shipping to ${shippingLocation} (${zipCode})`;
    } else if (zipCode) {
      shippingLabel = `Shipping (${zipCode})`;
    }

    return `
      <div class="summary-content summary-thankyou">
        <div class="thank-you-header-box mb-md-6 mb-4">
          <h2 class="h3 mb-3 thank-you-title">Thank you!</h2>
          <p class="thank-you-description mb-0" style="letter-spacing: -0.02em;">Your order summary has been emailed, and a dedicated team member will contact you shortly to guide you through the next steps and help you understand the entire process, ensuring everything is handled seamlessly.</p>
        </div>
        
        <div class="summary-product-section mb-2">
          <div class="d-flex align-items-end justify-content-between mb-2">
            <h3 class="h4 mb-0 summary-product-title">${productTitle}</h3>
            <span class="fw-medium" style="line-height: 1.4; letter-spacing: -0.02em;">${formatPrice(productPrice)}</span>
          </div>
          ${configSummary ? `
          <div class="d-flex align-items-center justify-content-between">
            <p class="fs-sm text-gray-600 mb-0" style="letter-spacing: -0.01em;">${configSummary}</p>
            <a href="#" class="text-decoration-underline" data-edit-design>Edit Design</a>
          </div>
          ` : ''}
        </div>
        
        <div class="summary-price-details-box mb-md-4 mb-3" data-price-details-box>
          <button class="price-details-toggle d-flex align-items-center justify-content-between" data-toggle-price-details>
            <span>Price details</span>
            <span class="d-flex align-items-center" data-toggle-icon>
              <svg aria-hidden="true" focusable="false" style="width:15px; height:15px;">
                <use href="#icon-modal-plus"></use>
              </svg>
            </span>
          </button>
          <div data-price-details-content>
            <div class="price-details-content">${this.buildPriceDetailsHTML()}</div>
          </div>
        </div>
        
        <div class="summary-line-items" data-summary-line-items>
          <div class="summary-line-item d-flex align-items-center justify-content-between gap-2 py-md-4 py-3" data-summary-line-item="shipping">
            <span data-line-item-label>${shippingLabel}</span>
            <span class="fw-medium" data-line-item-price>${shippingPrice > 0 ? formatPrice(shippingPrice) : 'Included'}</span>
          </div>
          ${installationPrice > 0 && installationName ? `
          <div class="summary-line-item d-flex align-items-center justify-content-between gap-2 py-md-4 py-3" data-summary-line-item="installation">
            <span class="line-item-label" data-line-item-label>${installationName} <span class="line-item-note" data-line-item-note>(subject to availability)</span></span>
            <span class="line-item-price fw-medium" data-line-item-price>${formatPrice(installationPrice)}</span>
          </div>` : ''}
          <div class="summary-line-item py-md-4 py-3" data-summary-line-item="permit">
            <div class="d-flex align-items-center justify-content-between gap-2 mb-2" data-line-item-main>
              <span data-line-item-label>${this.permitPlanSelected === 'include' ? 'Include Permit Plans' : 'No Permit Plans'}</span>
              <span class="fw-medium" data-permit-price-display>${this.permitPlanPrice > 0 ? formatPrice(this.permitPlanPrice) : 'No charge'}</span>
            </div>
            <button class="bg-transparent border-0 fw-light text-decoration-underline p-0" style="letter-spacing: -0.02em;" data-change-permit-plans>Change permit plans</button>
          </div>
          <div class="summary-line-item py-md-4 py-3" data-summary-line-item="foundation">
            <div class="d-flex align-items-center justify-content-between gap-2 mb-2" data-line-item-main>
              <span class="line-item-label" data-foundation-label>Foundation TBD</span>
              <span class="fw-medium" data-line-item-price>Cost not included</span>
            </div>
            <button class="bg-transparent border-0 fw-light text-decoration-underline p-0" style="letter-spacing: -0.02em;" data-change-foundation-type>Change foundation type</button>
          </div>
        </div>
        
        <div class="pt-lg-4 pb-lg-5 py-3" data-summary-total>
          <div class="d-flex justify-content-between mb-2" data-summary-total-row>
            <h5 class="mb-0" data-total-label>Estimated Total</h5>
            <h5 class="mb-0" data-summary-total-price>${formatPrice(estimatedTotal)}</h5>
          </div>
          <p class="fs-sm mb-0" data-total-note style="letter-spacing: -0.01em;">${this.getTotalNoteText()}</p>
        </div>
        
        ${this.buildCTASectionHTML()}
        ${this.buildDesignDetailsHTML(variantTitle)}
      </div>
    `;
  }

  buildConfigSummaryString() {
    const parts = [];
    // Add all selected config values that exist
    const summaryKeys = ['interiorPackage', 'frontPanel', 'siding', 'sidingColor'];
    summaryKeys.forEach(key => {
      const value = this.currentConfig[key];
      if (value && !parts.includes(formatOptionValue(value))) {
        parts.push(formatOptionValue(value));
      }
    });
    return parts.join(' | ');
  }

  buildPriceDetailsHTML() {
    const variantTitle = this.modelsData?.name || '';
    const sqFt = this.modelsData?.squareFootage || '';
    
    // Build features from actual config values
    const configFeatures = [
      this.currentConfig.interiorPackage,
      this.currentConfig.frontPanel,
      this.currentConfig.siding
    ].filter(Boolean).map(formatOptionValue);

    // Build label with optional sqFt
    const labelParts = [variantTitle];
    if (sqFt) labelParts.push(`(${sqFt} sq. ft.)`);
    const mainLabel = labelParts.filter(Boolean).join(' ');

    let html = '';
    
    // Only show main section if we have a label
    if (mainLabel) {
      html += `
        <div class="price-detail-item price-detail-main">
          <div class="price-detail-info">
            <span class="d-inline-block mb-2">${mainLabel}</span>
            ${configFeatures.map(f => `<span class="price-detail-feature fs-sm text-gray-600 mb-1 d-block" style="letter-spacing: -0.01em;">${f}</span>`).join('')}
          </div>
          <span>${formatPrice(this.basePrice)}</span>
        </div>
      `;
    }

    // Get option display name from steps
    const getOptionDisplayName = (sectionKey, value) => {
      const option = this.findOptionInSteps(sectionKey, value);
      return option?.displayName || formatOptionValue(value);
    };

    // Add price details for selected options that have prices
    // Include all configurable options for Signature model
    const detailKeys = [
      { key: 'interiorPackage', label: 'Interior Package' },
      { key: 'frontPanel', label: 'Front Panel' },
      { key: 'backPanel', label: 'Back Panel' },
      { key: 'leftPanel', label: 'Left Panel' },
      { key: 'rightPanel', label: 'Right Panel' },
      { key: 'roof', label: 'Roof' },
      { key: 'roofAddon', label: 'Roof Addon' },
      { key: 'siding', label: 'Siding' },
      { key: 'sidingColor', label: 'Siding Color' },
      { key: 'exteriorAddon', label: 'Exterior Addon' },
      { key: 'trimColor', label: 'Trim' },
      { key: 'doorColor', label: 'Door' },
      { key: 'eavesColor', label: 'Eaves' },
      { key: 'mainFloorFinish', label: 'Main Flooring' },
      { key: 'bathFloorFinish', label: 'Bath Flooring' },
      { key: 'fixtureFinish', label: 'Fixtures' },
      { key: 'counterFinish', label: 'Countertop' },
      { key: 'cabinetFinish', label: 'Cabinets' }
    ];

    detailKeys.forEach(({ key, label }) => {
      const value = this.currentConfig[key];
      if (!value) return;
      
      const option = this.findOptionInSteps(key, value);
      const displayName = option?.displayName || formatOptionValue(value);
      const price = option?.price || 0;
      
      html += `
        <div class="price-detail-item">
          <span class="price-detail-label">${label}: ${displayName}</span>
          <span class="price-detail-price">${formatPrice(price)}</span>
        </div>
      `;
    });

    return html;
  }

  buildCTASectionHTML() {
    // Get CTA config from modelConfig or use Signature-specific defaults
    const productName = this.modelsData?.name || this.selectedVariant?.title || 'Signature';
    
    // Signature-specific default configuration
    const defaultCta = {
      calculator: {
        icon: `<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="40" height="40" rx="8" fill="#FDB913" fill-opacity="0.15"/>
          <path d="M13 14h14v12H13V14z" stroke="#FDB913" stroke-width="1.5" fill="none"/>
          <path d="M16 18h8M16 21h8M16 24h4" stroke="#FDB913" stroke-width="1.5" stroke-linecap="round"/>
        </svg>`,
        title: 'Need A Quick Estimate?',
        link: '/pages/project-cost-calculator',
        linkText: 'TRY PROJECT COST CALCULATOR'
      },
      financing: {
        logos: [
          `<img src="https://cdn.shopify.com/s/files/1/0969/5304/1190/files/acorn-finance-logo.png?v=1735000000" alt="Acorn Finance" style="height: 24px; width: auto;">`,
          `<img src="https://cdn.shopify.com/s/files/1/0969/5304/1190/files/renofi-logo.png?v=1735000000" alt="RenoFi" style="height: 24px; width: auto;">`
        ],
        title: 'Monthly Financing Available!',
        subtitle: 'Plan smarter with the right financing',
        link: '/pages/financing',
        linkText: 'EXPLORE FINANCING OPTIONS'
      },
      consultation: {
        title: 'Schedule a FREE Online Consultation',
        description: 'Select the available time that works for you to explore your project goals, budget, build site, and initial design.',
        link: '/pages/consultation',
        buttonText: 'BOOK A FREE CONSULTATION'
      }
    };
    
    // PDF icon SVG for downloads
    const pdfIcon = `<svg viewBox="0 0 24 24" fill="none" style="width: 24px; height: 24px;">
      <g clip-path="url(#clip0_1618_10352)">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M0.67107 22.95C-1.00893 20.91 0.77757 18.48 2.60607 17.445C3.62157 16.8735 4.88607 16.476 6.25107 16.101C5.84487 16.8749 5.3967 17.6261 4.90857 18.351C4.50698 18.5122 4.11573 18.6981 3.73707 18.9075C2.77407 19.4505 1.48707 20.4 2.27757 21.5925C3.04707 22.7535 4.28757 21.477 4.87257 20.8035C5.61957 20.0415 6.46257 18.8385 7.21257 17.5185C8.00607 16.1235 8.72757 14.5485 9.41757 12.8835C10.0401 11.3835 10.5936 9.83855 11.0076 8.39855C11.4276 6.95855 11.7111 5.62505 11.8161 4.50005C11.9016 3.56555 11.8611 2.19005 10.6311 2.37005C9.40107 2.55005 9.41607 3.99005 9.70857 5.11505C9.79457 5.45005 9.90257 5.79755 10.0326 6.15755C9.94057 6.58855 9.82907 7.04355 9.69807 7.52255C9.58807 7.91855 9.46807 8.32405 9.33807 8.73905C8.80876 7.706 8.38995 6.61999 8.08857 5.49905C7.56357 3.47405 7.54107 0.654046 10.2636 0.0690461C12.9861 -0.515954 13.6686 2.57405 13.4886 4.55405C13.3386 6.15905 12.8931 8.12405 12.2181 10.149C12.6501 10.8645 13.1211 11.5815 13.6206 12.279C14.1456 13.0155 14.8356 13.7685 15.4056 14.424C17.4156 14.274 19.3956 14.373 20.8806 14.6535C22.9206 15.039 24.7656 16.3185 23.6856 18.7635C22.6041 21.2085 19.9356 20.8485 18.1656 19.71C16.6893 18.7558 15.3942 17.5471 14.3406 16.14H14.3361C13.2173 14.4622 12.1907 12.7246 11.2611 10.935C10.8171 12.2745 10.1661 14.07 9.61107 15.27C10.3206 15.105 11.0661 14.97 11.8011 14.8575C12.1026 15.3645 12.3906 15.8595 12.6861 16.3395C12.4511 16.3745 12.2061 16.4135 11.9511 16.4565C11.0281 16.6135 10.1231 16.8015 9.23607 17.0205C9.01207 17.4505 8.79507 17.8745 8.58507 18.2925C7.83657 19.7745 7.14657 21.1425 6.29007 22.1175C4.92057 23.6775 2.36007 24.9525 0.68007 22.9125L0.67107 22.95ZM16.4211 15.93C17.3196 16.848 18.1611 17.55 18.9861 18.09C19.9881 18.735 21.1461 19.1115 21.6861 17.985C22.2366 16.8555 21.2196 16.29 20.1561 16.08C19.1076 15.8805 17.8611 15.8715 16.4061 15.924L16.4211 15.93Z" fill="black"/>
      </g>
      <defs>
        <clipPath id="clip0_1618_10352">
          <rect width="24" height="24" fill="white"/>
        </clipPath>
      </defs>
    </svg>`;
    
    // Build downloads from variant metafield documents
    const variantDocs = (this.variantDocuments || []).map(doc => ({
      icon: pdfIcon,
      title: doc.alt || doc.filename || `${productName} Document`,
      url: doc.url
    }));
    
    const ctaConfig = this.modelConfig?.cta || defaultCta;
    const downloads = variantDocs.length ? variantDocs : (this.modelConfig?.downloads || []);

    let html = '';

    // CTA Cards section
    const calculator = ctaConfig.calculator || defaultCta.calculator;
    const financing = ctaConfig.financing || defaultCta.financing;
    
    html += `<div class="d-flex flex-column gap-2 pb-5" data-summary-cta-section>`;

     // Consultation section
     const consultation = ctaConfig.consultation || defaultCta.consultation;
     html += `
       <div class="py-4 border-top border-gray-200" data-summary-consultation>
         <h5 class="mb-2 d-none" data-consultation-title>${consultation.title}</h5>
         <p class="mb-lg-5 mb-3 pe-xl-6" style="letter-spacing: -0.02em;" data-consultation-description>${consultation.description}</p>
         <a href="${consultation.link}" class="btn btn-primary w-100" data-consultation-btn target="_blank">${consultation.buttonText}</a>
       </div>
     `;
    
    // Calculator card
    html += `
      <div class="calculator-card position-relative overflow-hidden d-flex flex-wrap align-items-center justify-content-between bg-primary-100 rounded gap-2" data-cta-card="calculator">
        <div class="d-flex align-items-center gap-lg-6 gap-3 me-3" data-cta-content>
          <div class="calculator-icon position-absolute">
            <img src="https://cdn.shopify.com/s/files/1/0969/5304/1190/files/calculator.svg?v=1767594243" alt="Calculator" title="Calculator" class="w-100 h-100"/>
          </div>
          <span class="fw-medium" data-cta-text>${calculator.title}</span>
        </div>
        <a href="${calculator.link}" class="d-flex align-items-center gap-2 text-decoration-none fw-medium" data-cta-link target="_blank">
          ${calculator.linkText}
          <svg viewBox="0 0 11 11" fill="none" style="width: 11px; height: 11px;">
            <path d="M5.5 0.5L10.5 5.5M10.5 5.5L5.5 10.5M10.5 5.5L0.5 5.5" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </a>
      </div>
    `;

    

    // Financing card (hidden by default like Aspect)
    html += `
      <div class="financing-card d-flex flex-wrap align-items-center justify-content-between bg-secondary rounded gap-2 d-none" data-cta-card="financing">
        <div class="d-flex align-items-center gap-lg-6 gap-3 me-3" data-cta-content>
          <div class="d-flex flex-column gap-2" data-financing-logos>
            <img src="https://cdn.shopify.com/s/files/1/0969/5304/1190/files/acorn.png?v=1767596474" class="acorn-logo" alt="Acorn Finance" title="Acorn Finance"/>
            <img src="https://cdn.shopify.com/s/files/1/0969/5304/1190/files/renofi.png?v=1767596473" class="renofi-logo" alt="RenoFi" title="RenoFi"/>
          </div>
          <div class="d-flex flex-column" data-financing-text>
            <p class="fw-medium mb-1">${financing.title}</p>
            <span class="fs-sm" style="letter-spacing: -0.01em;">${financing.subtitle}</span>
          </div>
        </div>
        <a href="${financing.link}" class="d-flex align-items-center gap-2 text-decoration-none text-black fw-medium" data-cta-link target="_blank">
          ${financing.linkText}
          <svg viewBox="0 0 11 11" fill="none" style="width: 11px; height: 11px;">
            <path d="M5.5 0.5L10.5 5.5M10.5 5.5L5.5 10.5M10.5 5.5L0.5 5.5" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </a>
      </div>
    `;

    html += `</div>`;

    // Downloads section
    if (downloads.length) {
      html += `
        <div class="py-4 border-top border-gray-200" data-summary-downloads>
          <h5 class="mb-3" data-downloads-title>Downloads</h5>
          ${downloads.map(dl => `
            <a href="${dl.url || '#'}" class="d-flex align-items-center gap-3 text-decoration-none text-black" data-download-link target="_blank">
              <span data-download-icon>${dl.icon}</span>
              <span class="text-decoration-underline">${dl.title || ''}</span>
            </a>
          `).join('')}
        </div>
      `;
    }

    return html;
  }

  buildDesignDetailsHTML(variantTitle) {
    const config = this.currentConfig || {};
    const modelData = this.modelsData || {};

    // Helper to get option data (including hex, displayName) from steps
    const getOptionData = (sectionKey, value) => {
      if (!value) return null;
      for (const step of this.steps) {
        // Check panel-based sections
        if (step.sections) {
          const section = step.sections.find(s => s.id === sectionKey);
          if (section) {
            const option = section.options?.find(o => o.value === value);
            if (option) return option;
          }
        }
      }
      return null;
    };

    // Helper to render material item with texture image preview (for flooring and siding)
    const renderTextureItem = (label, value, sectionKey) => {
      if (!value) return '';
      const option = getOptionData(sectionKey, value);
      // Get texture image from option - check multiple possible properties
      const textureImage = option?.texture || option?.thumbnail || option?.image || option?.preview;
      const displayName = option?.displayName || formatOptionValue(value);
      
      if (textureImage) {
        // Render with texture image
        return `
          <div class="material-item d-flex align-items-center gap-2 mb-3">
            <div class="material-swatch material-swatch--texture overflow-hidden">
              <img src="${textureImage}" alt="${displayName}" loading="lazy" style="width: 100%; height: 100%; object-fit: cover;">
            </div>
            <div class="material-info">
              <span class="d-block mb-1 fs-sm" style="letter-spacing: -0.01em;">${label}</span>
              <span class="d-block">${displayName}</span>
            </div>
          </div>
        `;
      } else {
        // Fallback to hex color if no texture image available
        const hex = option?.hex || option?.color?.hex || '#888888';
        return `
          <div class="material-item d-flex align-items-center gap-2 mb-3">
            <div class="material-swatch overflow-hidden" style="background: ${hex};"></div>
            <div class="material-info">
              <span class="d-block mb-1 fs-sm" style="letter-spacing: -0.01em;">${label}</span>
              <span class="d-block">${displayName}</span>
            </div>
          </div>
        `;
      }
    };

    // Helper to render material item only if value exists (with solid color)
    const renderMaterialItem = (label, value, sectionKey) => {
      if (!value) return '';
      const option = getOptionData(sectionKey, value);
      const hex = option?.hex || option?.color?.hex || '#888888';
      return `
        <div class="material-item d-flex align-items-center gap-2 mb-3">
          <div class="material-swatch overflow-hidden" style="background: ${hex};"></div>
          <div class="material-info">
            <span class="d-block mb-1 fs-sm" style="letter-spacing: -0.01em;">${label}</span>
            <span class="d-block">${option?.displayName || formatOptionValue(value)}</span>
          </div>
        </div>
      `;
    };

    // Helper to render color item only if value exists (RGB color representation)
    const renderColorItem = (label, sectionKey, value) => {
      if (!value) return '';
      const option = getOptionData(sectionKey, value);
      const hex = option?.hex || option?.color?.hex || '#888888';
      return `
        <div class="color-item d-flex align-items-center gap-2">
          <div class="color-swatch overflow-hidden" style="background: ${hex};"></div>
          <div class="color-info">
            <span class="d-block mb-1 fs-sm">${label}</span>
            <span class="d-block">${option?.displayName || formatOptionValue(value)}</span>
          </div>
        </div>
      `;
    };

    // Helper to render detail row only if value exists
    const renderDetailRow = (label, value) => {
      if (!value) return '';
      return `
        <div class="design-detail-col">
          <h6 class="mb-3">${label}</h6>
          <p class="mb-0">${formatOptionValue(value)}</p>
        </div>
      `;
    };

    // Build model specs line
    const specParts = [];
    if (modelData.depth) specParts.push(`Depth: ${modelData.depth}`);
    if (modelData.length) specParts.push(`Length: ${modelData.length}`);
    if (modelData.squareFootage) specParts.push(`${modelData.squareFootage} sq. ft.`);
    const specsLine = specParts.length ? `<p>${specParts.join(' | ')}</p>` : '';

    // Build model features line
    const featureParts = [];
    if (config.frontPanel) featureParts.push(formatOptionValue(config.frontPanel));
    if (config.siding) featureParts.push(formatOptionValue(config.siding));
    const featuresLine = featureParts.length ? `<p class="mb-2">${featureParts.join(' | ')}</p>` : '';

    // Build interior line
    const interiorParts = [];
    if (config.interiorPackage) interiorParts.push(formatOptionValue(config.interiorPackage));
    const interiorLine = interiorParts.length ? `<p>${interiorParts.join(' | ')}</p>` : '';

    // Get color values
    const sidingColorValue = config.sidingColor;

    // Helper to get config value - checks multiple possible keys
    const getConfigValue = (primaryKey, ...altKeys) => {
      if (config[primaryKey]) return config[primaryKey];
      for (const key of altKeys) {
        if (config[key]) return config[key];
      }
      return null;
    };

    // Get flooring value - check multiple possible config keys
    const flooringConfigValue = getConfigValue('flooring', 'mainFloorFinish', 'mainFlooring', 'floorFinish', 'floor');
    
    // Check if interior package is "Shell Only" - normalize the value for comparison
    const interiorPackageValue = config.interiorPackage || '';
    const isShellOnly = interiorPackageValue && 
      String(interiorPackageValue).toLowerCase().replace(/[\s_-]/g, '').includes('shellonly');

    // Determine the correct section key for flooring lookup
    const flooringSectionKey = config.flooring ? 'flooring' : 
                               config.mainFloorFinish ? 'mainFloorFinish' : 
                               config.mainFlooring ? 'mainFlooring' : 
                               config.floorFinish ? 'floorFinish' : 
                               config.floor ? 'floor' : 'flooring';

    // Build flooring section - only if NOT Shell Only and flooring has a value
    let flooringItems = '';
    if (!isShellOnly && flooringConfigValue) {
      flooringItems = renderTextureItem('Flooring', flooringConfigValue, flooringSectionKey);
    }

    // Build siding section with texture preview
    let sidingItem = '';
    if (config.siding) {
      sidingItem = renderTextureItem('Siding', config.siding, 'siding');
    }

    // Debug: Log all config values to help identify correct keys
    console.log('[Signature Summary] ========== CONFIG DEBUG ==========');
    console.log('[Signature Summary] All config keys:', Object.keys(config));
    console.log('[Signature Summary] Full config:', JSON.stringify(config, null, 2));
    console.log('[Signature Summary] Flooring value:', flooringConfigValue, '(key:', flooringSectionKey + ')');
    console.log('[Signature Summary] isShellOnly:', isShellOnly);
    // Log any key containing specific terms
    Object.keys(config).forEach(key => {
      const keyLower = key.toLowerCase();
      if (keyLower.includes('wainscot') || keyLower.includes('roof') || keyLower.includes('floor')) {
        console.log(`[Signature Summary] Found relevant key: ${key} = ${config[key]}`);
      }
    });
    console.log('[Signature Summary] =================================');

    // Build colors section (RGB color representation for all color options)
    // Only include colors that are USER-SELECTABLE in the Signature model
    // Note: Fascia and Soffit are NOT user-selectable options in Signature
    const colorItemsArray = [];
    
    // Add each color option only if it exists in the config
    if (sidingColorValue) {
      colorItemsArray.push(renderColorItem('Siding Color', 'sidingColor', sidingColorValue));
    }
    if (config.doorColor) {
      colorItemsArray.push(renderColorItem('Door', 'doorColor', config.doorColor));
    }
    if (config.trimColor) {
      colorItemsArray.push(renderColorItem('Trim', 'trimColor', config.trimColor));
    }
    if (config.eavesColor || config.eaves) {
      const eavesValue = config.eavesColor || config.eaves;
      const eavesSectionKey = config.eavesColor ? 'eavesColor' : 'eaves';
      colorItemsArray.push(renderColorItem('Eaves', eavesSectionKey, eavesValue));
    }
    // Window casement color is auto-derived from trim color in the 3D viewer
    // (not user-selectable), so it's not shown in the summary.
    if (config.accentColor) {
      colorItemsArray.push(renderColorItem('Accent', 'accentColor', config.accentColor));
    }
    
    const colorItems = colorItemsArray.join('');

    // Find wainscot value - check exteriorAddon (wainscot is stored there) or keys containing "wainscot"
    let wainscotValue = null;
    let wainscotKey = null;
    
    // Check exteriorAddon first - wainscot is typically stored here with value like "metal-wainscot"
    if (config.exteriorAddon) {
      const addonValue = String(config.exteriorAddon).toLowerCase();
      if (addonValue.includes('wainscot')) {
        wainscotValue = config.exteriorAddon;
        wainscotKey = 'exteriorAddon';
        console.log('[Signature Summary] Found wainscot in exteriorAddon:', wainscotValue);
      }
    }
    
    // Fallback: check for any key containing "wainscot"
    if (!wainscotValue) {
      for (const key of Object.keys(config)) {
        if (key.toLowerCase().includes('wainscot')) {
          wainscotValue = config[key];
          wainscotKey = key;
          console.log('[Signature Summary] Found wainscot key:', key, 'value:', wainscotValue);
          break;
        }
      }
    }

    // Find roof value - check all possible config keys
    let roofValue = config.roof || config.roofStyle || config.roofType || config.roofOptions || config.roofOption;
    if (!roofValue) {
      for (const key of Object.keys(config)) {
        if (key.toLowerCase().includes('roof')) {
          roofValue = config[key];
          console.log('[Signature Summary] Found roof key:', key, 'value:', roofValue);
          break;
        }
      }
    }

    // Build detail options rows - include all configurable options for Signature
    const detailRows = [
      { label: 'INTERIOR PACKAGE', value: config.interiorPackage },
      { label: 'FRONT PANEL', value: config.frontPanel },
      { label: 'BACK PANEL', value: config.backPanel },
      { label: 'LEFT PANEL', value: config.leftPanel },
      { label: 'RIGHT PANEL', value: config.rightPanel },
      { label: 'ROOF OPTIONS', value: roofValue },
      { label: 'ROOF ADDON', value: config.roofAddon },
      { label: 'WAINSCOT', value: wainscotValue },
      { label: 'EXTERIOR ADDON', value: config.exteriorAddon && !String(config.exteriorAddon).toLowerCase().includes('wainscot') ? config.exteriorAddon : null }
    ].filter(r => r.value);

    // Get includes/excludes from modelConfig or use defaults
    const includes = this.modelConfig?.includes || [
      'Exterior Shell',
      'Interior Framing',
      'Interior Insulation and flooring',
      'Electrical, lighting',
      'Door and window hardware'
    ];
    const excludes = this.modelConfig?.excludes || [
      'Foundation and site utilities',
      'Drywall (included with ProAssembly service)',
      'Permit fees',
      'Sales tax as applicable'
    ];

    let html = `<div class="summary-design-details"><h5 class="mb-md-5 mb-3">Design Details</h5>`;

    // MODEL & INTERIOR section
    html += `
      <div class="design-details-grid">
        <div class="design-detail-col">
          <h6 class="mb-md-3 mb-2">MODEL</h6>
          ${variantTitle ? `<p class="mb-2">${variantTitle}</p>` : ''}
          ${featuresLine}
          ${specsLine}
        </div>
        ${interiorLine ? `
        <div class="design-detail-col">
          <h6 class="mb-md-3 mb-2">INTERIOR</h6>
          ${interiorLine}
        </div>
        ` : ''}
      </div>
    `;

    // Detail options rows (in pairs)
    if (detailRows.length > 0) {
      for (let i = 0; i < detailRows.length; i += 2) {
        html += `<div class="design-details-grid design-details-options">`;
        html += renderDetailRow(detailRows[i].label, detailRows[i].value);
        if (detailRows[i + 1]) {
          html += renderDetailRow(detailRows[i + 1].label, detailRows[i + 1].value);
        }
        html += `</div>`;
      }
    }

    html += `</div>`;

    // Flooring & Siding section (with texture previews)
    if (flooringItems || sidingItem) {
      html += `<div class="summary-materials-section"><div class="design-details-grid">`;
      if (flooringItems) {
        html += `<div class="design-detail-col"><h6 class="mb-3">FLOORING</h6>${flooringItems}</div>`;
      }
      if (sidingItem) {
        html += `<div class="design-detail-col"><h6 class="mb-3">SIDING</h6>${sidingItem}</div>`;
      }
      html += `</div></div>`;
    }

    // Colors section (RGB color representation for all other options)
    if (colorItems) {
      html += `
        <div class="summary-colors-section">
          <h6 class="mb-3">COLORS</h6>
          <div class="colors-grid">${colorItems}</div>
        </div>
      `;
    }

    // Elevations section - show images based on selected panel options
    const elevationImages = this.getElevationImages();
    if (elevationImages.front || elevationImages.back || elevationImages.left || elevationImages.right) {
      html += `
        <div class="summary-elevations-section d-flex flex-column gap-4">
          ${elevationImages.front ? `
          <div class="elevation-view">
            <h6 class="mb-3">FRONT</h6>
            <div class="elevation-image"><img src="${elevationImages.front}" alt="Front elevation" loading="lazy"></div>
          </div>
          ` : ''}
          ${elevationImages.back ? `
          <div class="elevation-view">
            <h6 class="mb-3">BACK</h6>
            <div class="elevation-image"><img src="${elevationImages.back}" alt="Back elevation" loading="lazy"></div>
          </div>
          ` : ''}
          ${elevationImages.left ? `
          <div class="elevation-view">
            <h6 class="mb-3">LEFT</h6>
            <div class="elevation-image"><img src="${elevationImages.left}" alt="Left elevation" loading="lazy"></div>
          </div>
          ` : ''}
          ${elevationImages.right ? `
          <div class="elevation-view">
            <h6 class="mb-3">RIGHT</h6>
            <div class="elevation-image"><img src="${elevationImages.right}" alt="Right elevation" loading="lazy"></div>
          </div>
          ` : ''}
        </div>
      `;
    }

    // Includes/Excludes section (always show)
    html += `
      <div class="summary-includes-excludes">
        <div class="includes-excludes-grid">
          <div class="includes-col mb-sm-0 mb-5">
            <h6 class="mb-3">INCLUDES</h6>
            <ul class="ps-xl-5 mb-0">${includes.map(item => `<li>${item}</li>`).join('')}</ul>
          </div>
          <div class="excludes-col">
            <h6 class="mb-3">EXCLUDES</h6>
            <ul class="ps-xl-5 mb-0">${excludes.map(item => `<li>${item}</li>`).join('')}</ul>
          </div>
        </div>
      </div>
    `;

    return html;
  }

  /**
   * Get elevation images from selected panel options
   * Uses the image/thumbnail/texture property from step 2 panel options
   * Falls back to checking modelConfig.images.elevations if options don't have images
   */
  getElevationImages() {
    const config = this.currentConfig || {};
    const images = {
      front: null,
      back: null,
      left: null,
      right: null
    };

    // Get image for each panel from the selected option
    const panelKeys = [
      { key: 'frontPanel', side: 'front' },
      { key: 'backPanel', side: 'back' },
      { key: 'leftPanel', side: 'left' },
      { key: 'rightPanel', side: 'right' }
    ];

    panelKeys.forEach(({ key, side }) => {
      const value = config[key];
      if (value) {
        const option = this.findOptionInSteps(key, value);
        
        // Debug: Log what properties the option has
        if (option) {
          console.log(`[Signature Summary] ${key} option properties:`, {
            value: option.value,
            displayName: option.displayName,
            image: option.image,
            thumbnail: option.thumbnail,
            texture: option.texture,
            elevationImage: option.elevationImage,
            panelImage: option.panelImage,
            allKeys: Object.keys(option)
          });
        }
        
        // Check multiple possible image properties
        const optionImage = option?.image || option?.elevationImage || option?.panelImage || option?.thumbnail || option?.texture;
        if (optionImage) {
          images[side] = optionImage;
        }
      }
    });

    // Fallback: Check if modelConfig has elevation images (like Aspect)
    // This allows using dedicated elevation images if configured in JSON
    if (!images.front && !images.back && !images.left && !images.right) {
      const selectedKey = config.frontPanel || config.exterior || 'default';
      const configElevations = this.modelConfig?.images?.elevations?.[selectedKey] || 
                               this.modelConfig?.images?.elevations?.default ||
                               this.modelConfig?.elevationImages;
      
      console.log('[Signature Summary] Checking modelConfig for elevation images:', {
        selectedKey,
        hasImagesElevations: !!this.modelConfig?.images?.elevations,
        hasElevationImages: !!this.modelConfig?.elevationImages,
        configElevations
      });
      
      if (configElevations) {
        images.front = configElevations.front || null;
        images.back = configElevations.back || null;
        images.left = configElevations.left || null;
        images.right = configElevations.right || null;
      }
    }

    console.log('[Signature Summary] Final elevation images:', images);
    return images;
  }

  findOptionInSteps(sectionKey, value) {
    for (const step of this.steps) {
      // Check panel-based format (sections)
      if (step.sections) {
        const section = step.sections.find(s => s.id === sectionKey);
        if (section) {
          const option = section.options?.find(opt => opt.value === value);
          if (option) return option;
        }
      }
    }
    return null;
  }

  setupThankYouEventListeners(panel) {
    const editLink = $('[data-edit-design]', panel);
    console.log('[Signature] setupThankYouEventListeners - editLink found:', !!editLink);
    if (editLink) {
      editLink.addEventListener('click', (e) => {
        e.preventDefault();
        console.log('[Signature] Edit Design clicked');
        
        // Get the first step name from the steps array
        const firstStep = this.steps[0];
        const firstTabName = firstStep?.name?.toLowerCase() || firstStep?.id?.toLowerCase() || 'interior';
        console.log('[Signature] Switching to tab:', firstTabName, 'from steps:', this.steps.map(s => s.name || s.id));
        
        // Switch to the first step tab
        this.switchTab(firstTabName);
        
        // Show the footer navigation buttons
        const footer = q('[data-panel-footer]', '.panel-footer', this.step2Section);
        if (footer) footer.style.display = '';
        
        // Remove summary class from configurator main
        const configuratorMain = $('[data-configurator-main]', this.step2Section);
        if (configuratorMain) {
          configuratorMain.classList.remove('product-summary-tab');
        }
      });
    }

    const priceToggle = $('[data-toggle-price-details]', panel);
    const priceContent = $('[data-price-details-content]', panel);
    if (priceToggle && priceContent) {
      // Setup smooth dropdown animation styles (Signature-specific)
      priceContent.style.overflow = 'hidden';
      priceContent.style.transition = 'max-height 0.35s ease-in-out, opacity 0.3s ease-in-out';
      priceContent.style.maxHeight = '0';
      priceContent.style.opacity = '0';
      priceContent.style.display = 'block'; // Always block, control visibility via max-height
      
      priceToggle.addEventListener('click', () => {
        const isCollapsed = priceContent.style.maxHeight === '0px' || priceContent.style.maxHeight === '0';
        
        if (isCollapsed) {
          // Expand: measure content height and animate to it
          priceContent.style.maxHeight = priceContent.scrollHeight + 'px';
          priceContent.style.opacity = '1';
        } else {
          // Collapse: animate to 0
          priceContent.style.maxHeight = '0';
          priceContent.style.opacity = '0';
        }
        
        const icon = priceToggle.querySelector('[data-toggle-icon], .toggle-icon');
        if (icon) {
          const svg = icon.querySelector('svg use');
          if (svg) {
            svg.setAttribute('href', isCollapsed ? '#icon-modal-minus' : '#icon-modal-plus');
          } else {
            icon.textContent = isHidden ? '−' : '+';
          }
        }
      });
    }

    const changePermit = $('[data-change-permit-plans]', panel);
    if (changePermit) {
      changePermit.addEventListener('click', (e) => { 
        e.preventDefault(); 
        this.openPermitPlansDrawer(); 
      });
    }

    const changeFoundation = $('[data-change-foundation-type]', panel);
    if (changeFoundation) {
      changeFoundation.addEventListener('click', (e) => { 
        e.preventDefault(); 
        this.openFoundationTypeDrawer(); 
      });
    }
  }

  // ============================================================================
  // FAQ SECTION
  // ============================================================================

  appendFullWidthFAQ() {
    const existingFAQ = q('[data-faq-container]', '.full-width-faq-container');
    if (existingFAQ) existingFAQ.remove();

    let faqConfig = null;
    try {
      const faqConfigEl = $('[data-faq-config]', this.step2Section);
      if (faqConfigEl) faqConfig = JSON.parse(faqConfigEl.textContent);
    } catch (e) { /* use defaults */ }

    const faqTitle = faqConfig?.title || 'Frequently Asked Questions';
    const faqItems = faqConfig?.items || [];
    
    if (!faqItems.length) return;

    const faqContainer = document.createElement('div');
    faqContainer.className = 'full-width-faq-container';
    faqContainer.setAttribute('data-faq-container', '');
    faqContainer.innerHTML = `
      <div class="summary-faq-section" data-faq-section>
        <h3 class="faq-title" data-faq-title>${faqTitle}</h3>
        ${faqItems.map(item => `
          <div class="faq-item" data-faq-item>
            <button class="faq-question" data-faq-toggle>
              <span data-faq-question-text>${item.question}</span>
              <span class="faq-icon" data-faq-icon>+</span>
            </button>
            <div class="faq-answer" data-faq-answer style="display: none;">${item.answer}</div>
          </div>
        `).join('')}
      </div>
    `;

    const configuratorLayout = q('[data-configurator-layout]', '.configurator-layout', this.step2Section);
    if (configuratorLayout) {
      configuratorLayout.parentNode.insertBefore(faqContainer, configuratorLayout.nextSibling);
    } else {
      this.step2Section.appendChild(faqContainer);
    }

    faqContainer.querySelectorAll('[data-faq-toggle]').forEach(toggle => {
      toggle.addEventListener('click', () => {
        const content = toggle.nextElementSibling;
        const icon = toggle.querySelector('[data-faq-icon], .faq-icon');
        const isOpen = content.style.display !== 'none';
        content.style.display = isOpen ? 'none' : 'block';
        if (icon) icon.textContent = isOpen ? '+' : '−';
        toggle.classList.toggle('open', !isOpen);
      });
    });
  }

  // ============================================================================
  // PERMIT PLANS DRAWER - Using shared PermitManager
  // ============================================================================

  setupPermitPlansDrawer() {
    const rawSqFt = this.modelsData?.squareFootage;
    const parsedSqFt = typeof rawSqFt === 'string' ? parseInt(rawSqFt.replace(/[^0-9]/g, ''), 10) : parseInt(rawSqFt, 10);
    const sqFt = parsedSqFt || (parseInt(this.modelsData?.depth, 10) || 0) * (parseInt(this.modelsData?.length, 10) || 0);
    
    const defaultPermit = sqFt > 200 ? 'include' : 'exclude';
    console.log(`[Permit Debug] File Updated! Raw SQFT: ${rawSqFt}, Parsed: ${sqFt}, Calculated Default: ${defaultPermit}`);

    if (this.permitManager) {
      this.permitManager.defaultSelected = defaultPermit;
      this.permitManager.basePrice = this.modelConfig?.permitPlanPrice || 5995;
      this.permitManager.reset();
      return;
    }

    this.permitManager = new window.ConfiguratorShared.PermitManager({
      configurator: this,
      drawerId: 'permit-plans-drawer',
      basePrice: this.modelConfig?.permitPlanPrice || 4995, // Signature uses $4,995
      defaultSelected: defaultPermit,
      formatPrice: formatPrice,
      onSelect: () => this.recalculateSummaryTotal(),
      step2Section: this.step2Section
    });
    this.permitManager.setup();
  }

  openPermitPlansDrawer() {
    this.permitManager?.open();
  }

  updateSummaryPermitDisplay() {
    this.permitManager?.updateSummaryDisplay();
  }

  // ============================================================================
  // FOUNDATION TYPE DRAWER
  // ============================================================================

  setupFoundationTypeDrawer() {
    this.foundationManager = new window.ConfiguratorShared.FoundationManager({
      configurator: this,
      drawerId: 'foundation-type-drawer',
      step2Section: this.step2Section,
      labels: {
        'concrete-slab': 'Concrete Slab',
        'wood-frame': 'Wood Frame Floor',
        'not-sure': 'Not Sure?'
      }
    });
    this.foundationManager.setup();
  }

  openFoundationTypeDrawer() {
    this.foundationManager?.open();
  }

  updateFoundationDrawerSelection() {
    this.foundationManager?.updateSelection();
  }

  handleFoundationOptionSelect(card) {
    this.foundationManager?.handleSelect(card);
  }

  recalculateSummaryTotal() {
    const productPrice = this.basePrice + this.configPrice;
    const shippingPrice = normalizeNumber(this.finalPriceData?.shipping_cost, this.sectionSettings?.shippingPrice || this.modelConfig?.shippingPrice || 0);
    const installationOptions = this.modelConfig?.services?.installation?.options || 
                               this.sectionSettings?.services?.installation?.options || [];
    const selectedInstallation = installationOptions.find(opt => opt.value === this.currentConfig.installation);
    
    // Get installation price from API breakdown
    // For "shell-lifestyle" option, use lifestyle price from API breakdown
    let installationPrice = 0;
    const installationValue = this.currentConfig.installation || '';
    
    if (this.finalPriceData?.breakdown?.installation) {
      const breakdown = this.finalPriceData.breakdown.installation;
      
      // If user selected "shell-lifestyle", use lifestyle price from API breakdown
      if (installationValue.toLowerCase().includes('lifestyle')) {
        installationPrice = normalizeNumber(breakdown.lifestyle, 0);
      } else {
        // For other options, use the computed installation cost
        const computedCents = window.ConfiguratorShared.computeInstallationCost(
          breakdown,
          installationValue
        );
        installationPrice = computedCents > 0 ? computedCents : normalizeNumber(selectedInstallation?.price, 0);
      }
    } else {
      // Fallback if no API data available
      installationPrice = normalizeNumber(selectedInstallation?.price, 0);
    }
    const estimatedTotal = productPrice + shippingPrice + this.permitPlanPrice + installationPrice;

    const contentWrapper = $('[data-panel-content-wrapper]', this.step2Section);
    if (!contentWrapper) return;

    const totalDisplay = $('[data-summary-total-price]', contentWrapper);
    if (totalDisplay) totalDisplay.textContent = formatPrice(estimatedTotal);

    // Update ALL price elements (header and mobile footer)
    const headerPrices = $$('[data-configurator-price]', this.step2Section);
    headerPrices.forEach(el => el.textContent = formatPrice(estimatedTotal));
  }

  updateSummaryFoundationDisplay() {
    this.foundationManager?.updateSummaryDisplay();
  }

  // ============================================================================
  // UTILITIES
  // ============================================================================

  /**
   * Generate the total note text based on installation selection
   * Installation options:
   * - "shell" or "product-only" = Product Only (no assembly)
   * - "lifestyle" = ProAssembly: Shell Only (has assembly)
   * - "kitchenbath" = ProAssembly: Shell + Lifestyle Interior (has assembly)
   * @returns {string} HTML string for the total note
   */
  getTotalNoteText() {
    const installation = (this.currentConfig?.installation || '').toLowerCase();
    // "shell" and "product-only" mean no assembly, anything else means assembly is included
    const noAssemblyValues = ['shell', 'product-only', 'product_only', 'productonly', ''];
    const hasAssembly = !noAssemblyValues.includes(installation);
    const hasPermitPlans = this.permitPlanSelected === 'include';
    
    let includes = ['product', 'delivery'];
    if (hasAssembly) includes.push('assembly');
    if (hasPermitPlans) includes.push('permit plans');
    
    // Format the includes list with proper grammar
    let includesText;
    if (includes.length === 2) {
      includesText = includes.join(' and ');
    } else {
      const last = includes.pop();
      includesText = includes.join(', ') + ', and ' + last;
    }
    
    return `Includes ${includesText}.<br>Excludes foundation, site utilities, HVAC, decks, upgrades, permitting, local taxes.`;
  }

  updateURL(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url);
  }

  dispatchVariantChange(variantId, price) {
    document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variantId, price } }));
  }

  getSelectedVariantId() {
    const selectedCard = this.step1Section.querySelector('[data-variant-card].selected, .signature-variant-card.selected');
    return selectedCard?.dataset.variantId || null;
  }

  getConfiguration() {
    return {
      ...this.currentConfig,
      basePrice: this.basePrice,
      configPrice: this.configPrice,
      totalPrice: this.basePrice + this.configPrice,
      permitPlan: this.permitPlanSelected,
      permitPlanPrice: this.permitPlanPrice
    };
  }

  dispose() {
    if (this.step2ClickHandler && this.step2Section) {
      this.step2Section.removeEventListener('click', this.step2ClickHandler);
    }

    if (this.viewer3D) {
      this.viewer3D.dispose();
      this.viewer3D = null;
    }

    this.variantCards = [];
    this.thumbnails = [];
    this.steps = [];
    this.currentConfig = {};
    this.modelConfig = null;
    this.modelsData = null;
    this.userFormData = null;

    log('SignatureConfigurator disposed');
  }

  reset() {
    this.currentConfig = { ...this.defaultConfig };
    this.configPrice = 0;
    this.currentStep = 1;
    this.permitManager?.reset();
    this.foundationManager?.reset();
    this.userFormData = null;

    if (this.steps.length > 0) {
      this.switchTab(this.steps[0].name);
    }
  }

  /**
   * Restore default 8-4 column layout (from 6-6 thank you page layout)
   */
  restoreDefaultLayout() {
    const viewer = $('.configurator-viewer', this.step2Section);
    const panel = $('.configurator-panel', this.step2Section);
    if (viewer) {
      viewer.classList.remove('col-lg-6');
      viewer.classList.add('col-lg-8');
      // Remove centering styles from model viewer
      const modelContainer = $('[data-model-viewer], .model-viewer-container', viewer);
      if (modelContainer) {
        modelContainer.style.removeProperty('max-width');
        modelContainer.style.removeProperty('margin');
      }
    }
    if (panel) {
      panel.classList.remove('col-lg-6');
      panel.classList.add('col-lg-4');
    }

    // Trigger 3D viewer resize after layout change
    setTimeout(() => {
      if (this.viewer3D?.sceneManager?.onResize) {
        this.viewer3D.sceneManager.onResize();
      }
    }, 100);
  }

  handleStartOver() {
    this.reset();

    // Disable Summary tab immediately since form data is cleared
    this.updateSummaryTabState();
    
    const saveDesignBtn = $('[data-save-design]', this.step2Section);
    if (saveDesignBtn) {
      saveDesignBtn.textContent = 'SAVE DESIGN';
      delete saveDesignBtn.dataset.startOver;
    }

    const panelFooter = q('[data-panel-footer]', '.panel-footer', this.step2Section);
    if (panelFooter) panelFooter.style.display = '';

    const faqContainer = q('[data-faq-container]', '.full-width-faq-container');
    if (faqContainer) faqContainer.remove();

    // Restore layout to 8-4 columns
    this.restoreDefaultLayout();

    if (this.viewer3D) {
      this.applyConfigTo3D(this.currentConfig);
    }
  }
}

// ============================================================================
// INITIALIZATION
// ============================================================================

function initSignatureConfigurator() {
  if (!window.signatureConfigurator) {
    window.signatureConfigurator = new SignatureConfigurator();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSignatureConfigurator);
} else {
  initSignatureConfigurator();
}

})();

